import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Scanner } from '@yudiel/react-qr-scanner';
import { askSommelier } from '../services/api';
import axios from 'axios';
import QRCode from 'qrcode';
import OrbVisualizer from './OrbVisualizer';
import { toPng } from 'html-to-image';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const IDLE_CHECK_TIMEOUT = 0; // DISABLED - W IA only closes manually
const IDLE_GOODBYE_TIMEOUT = 0; // DISABLED - W IA only closes manually
const SILENCE_FRAMES = 90; // ~4.5s of silence to auto-stop recording (tripled for photo capture)

// Farewell phrases detection
const GOODBYE_PHRASES = [
  'tchau', 'adeus', 'ate logo', 'ate mais', 'ate a proxima', 'ate breve',
  'finalizar', 'encerrar', 'pode finalizar', 'pode encerrar',
  'obrigado por tudo', 'era isso', 'era so isso', 'so isso',
  'valeu', 'falou', 'flw', 'vlw', 'brigado', 'obrigada', 'obrigado',
  'nao preciso de mais nada', 'nao quero mais nada', 'por enquanto e so',
];

const isGoodbye = (text) => {
  const lower = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return GOODBYE_PHRASES.some(p => lower.includes(p));
};

const WIA = ({ currentMember }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [agentState, setAgentState] = useState('ready');
  const [statusText, setStatusText] = useState('Toque no orb');
  const [audioStream, setAudioStream] = useState(null);
  const [showTextInput, setShowTextInput] = useState(false);
  const [attachedImages, setAttachedImages] = useState([]); // Array of { base64, preview, name }
  const [authenticityResult, setAuthenticityResult] = useState(null); // { text, percentage }
  const [responseForPNG, setResponseForPNG] = useState(null); // { text, timestamp }
  const [showResponsePNGCard, setShowResponsePNGCard] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false); // Export tooltip state
  const [exportButtonRef, setExportButtonRef] = useState(null); // Ref for positioning
  const [showConversationHistory, setShowConversationHistory] = useState(false); // Modal to select conversation
  const [selectedMessageIndices, setSelectedMessageIndices] = useState([]); // Array of indices of selected messages to export
  const [isPaused, setIsPaused] = useState(false); // Mic paused state
  const [logoBase64, setLogoBase64] = useState(null); // W logo in base64 for PNG export
  const [ttsAnalyserData, setTtsAnalyserData] = useState({ grave: 0, agudo: 0 }); // TTS audio analysis for orb
  const [barExpanded, setBarExpanded] = useState(false); // Input bar expanded state
  const [showManual, setShowManual] = useState(false); // Manual/instructions modal
  const [barGlowing, setBarGlowing] = useState(false); // Bar glow effect when active
  const [typingPlaceholder, setTypingPlaceholder] = useState(''); // Rotating placeholder text
  const [waitingForAttachment, setWaitingForAttachment] = useState(false); // Waiting for user to attach image
  const [showVerifyScanner, setShowVerifyScanner] = useState(false); // QR scanner for seal verification
  const [verifyResult, setVerifyResult] = useState(null); // Result from QR verification

  // Pre-made prompts (shown only when image is attached)
  const IMAGE_PROMPTS = [
    'Analise o produto',
    'Harmonização',
    'Relógios',
    'Vinhos',
    'Whisky',
    'Charutos',
  ];

  const audioStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const ttsSourceRef = useRef(null);
  const ttsAudioRef = useRef(null);
  const memberRef = useRef(currentMember);
  const stateRef = useRef('ready');
  const isRecordingRef = useRef(false);
  const isSpeakingRef = useRef(false); // Flag para bloquear gravação durante fala
  const silenceCheckRef = useRef(null);
  const analyserRef = useRef(null);
  const mountedRef = useRef(true);
  const micReadyRef = useRef(false);
  const idleTimerRef = useRef(null);
  const idlePhaseRef = useRef('active'); // 'active' | 'checking' | 'done'
  const fileInputRef = useRef(null);
  const longPressTimerRef = useRef(null); // For long press detection
  const longPressTriggeredRef = useRef(false); // Flag to prevent click after long press
  const autoCollapseTimerRef = useRef(null); // For auto-collapse of input bar;
  const attachedImagesRef = useRef([]); // Ref para capturar as imagens no momento correto
  const authenticityCardRef = useRef(null); // Ref for PNG export
  const responsePNGCardRef = useRef(null); // Ref for response PNG export
  const messagesRef = useRef([]); // Keep track of messages for PNG generation
  const waitingForAttachmentRef = useRef(false); // Ref for waiting state

  useEffect(() => { memberRef.current = currentMember; }, [currentMember]);
  useEffect(() => { attachedImagesRef.current = attachedImages; }, [attachedImages]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { waitingForAttachmentRef.current = waitingForAttachment; }, [waitingForAttachment]);

  // Geolocation for contextual suggestions
  const geoRef = useRef(null);
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          let city = '', country = '';
          try {
            const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=pt`);
            const d = await r.json();
            city = d.address?.city || d.address?.town || d.address?.municipality || '';
            country = d.address?.country || '';
          } catch (e) {}
          geoRef.current = { lat: latitude, lng: longitude, city, country };
        },
        () => {},
        { enableHighAccuracy: false, timeout: 5000 }
      );
    }
  }, []);
  
  // Luxury curiosities shown while AI is processing
  const [curiosity, setCuriosity] = useState('');
  const curiosityTimerRef = useRef(null);

  const LUXURY_CURIOSITIES = [
    'O Romanée-Conti 1945 foi vendido por US$ 558.000 em leilão',
    'Um Cohiba Behike 56 leva 5 anos para ser enrolado a mão',
    'O Macallan 1926 é o whisky mais caro ja vendido: US$ 1.9 milhão',
    'A Patek Philippe fabrica apenas 62.000 relogios por ano',
    'O couro de um Birkin Hermès é selecionado entre 40.000 peles',
    'Um charuto cubano Montecristo usa folhas envelhecidas por 3 anos',
    'A trufa branca de Alba pode custar ate EUR 5.000 por quilo',
    'O Penfolds Grange 1951 é avaliado em mais de US$ 50.000',
    'Richard Mille produz apenas 5.000 relogios por ano',
    'O caviar Beluga iraniano custa ate US$ 35.000 por quilo',
    'A Rolls-Royce pinta cada carro com 7 camadas de verniz artesanal',
    'Um terno Brioni sob medida usa 18 horas de trabalho manual',
    'O diamante Hope tem 45.52 quilates e mais de 350 anos de historia',
    'A adega do Ritz Paris guarda mais de 350.000 garrafas',
    'Um Bugatti Chiron tem 1.500 cavalos e custa EUR 2.9 milhões',
    'O champagne Dom Perignon envelhece por minimo 7 anos na cave',
    'A Ferrari produz no maximo 14.000 carros por ano por exclusividade',
    'Um charuto Arturo Fuente OpusX usa folhas cultivadas por 10 anos',
    'O hotel Burj Al Arab em Dubai tem suite de US$ 24.000 por noite',
    'A caneta Montblanc Meisterstuck usa ouro 18k na pena',
  ];

  // Start/stop curiosity rotation based on processing state
  useEffect(() => {
    if (agentState === 'processing') {
      let idx = Math.floor(Math.random() * LUXURY_CURIOSITIES.length);
      setCuriosity(LUXURY_CURIOSITIES[idx]);
      curiosityTimerRef.current = setInterval(() => {
        idx = (idx + 1) % LUXURY_CURIOSITIES.length;
        setCuriosity(LUXURY_CURIOSITIES[idx]);
      }, 4000);
    } else {
      setCuriosity('');
      if (curiosityTimerRef.current) { clearInterval(curiosityTimerRef.current); curiosityTimerRef.current = null; }
    }
    return () => { if (curiosityTimerRef.current) clearInterval(curiosityTimerRef.current); };
  }, [agentState]);

  // Rotating placeholder text effect
  useEffect(() => {
    const phrases = [
      'Pergunte ao W...',
      'Harmonização',
      'Viagens de luxo',
      'Carros exclusivos',
      'Alta relojoaria',
      'Vinhos raros',
      'Whisky premium',
      'Charutos cubanos',
      'Gastronomia',
      'Joias',
      'Moda masculina',
      'Hoteis 5 estrelas',
      'Analise o produto'
    ];
    let phraseIndex = 0;
    let charIndex = 0;
    let isDeleting = false;
    let typingTimer;
    
    const typeEffect = () => {
      const currentPhrase = phrases[phraseIndex];
      
      if (!isDeleting) {
        // Typing
        setTypingPlaceholder(currentPhrase.substring(0, charIndex + 1));
        charIndex++;
        
        if (charIndex === currentPhrase.length) {
          // Finished typing, pause then start deleting
          isDeleting = true;
          typingTimer = setTimeout(typeEffect, 2000); // Pause before deleting
          return;
        }
        typingTimer = setTimeout(typeEffect, 80);
      } else {
        // Deleting
        setTypingPlaceholder(currentPhrase.substring(0, charIndex - 1));
        charIndex--;
        
        if (charIndex === 0) {
          // Finished deleting, move to next phrase
          isDeleting = false;
          phraseIndex = (phraseIndex + 1) % phrases.length;
          typingTimer = setTimeout(typeEffect, 500); // Pause before typing next
          return;
        }
        typingTimer = setTimeout(typeEffect, 40);
      }
    };
    
    typingTimer = setTimeout(typeEffect, 500);
    
    return () => {
      if (typingTimer) clearTimeout(typingTimer);
    };
  }, []);

  useEffect(() => { 
    mountedRef.current = true; 
    
    // Load W logo as base64 for PNG export
    const loadLogo = async () => {
      try {
        const response = await fetch('/w-logo.png');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => setLogoBase64(reader.result);
        reader.readAsDataURL(blob);
      } catch (e) {
        console.log('[WIA] Could not load logo:', e);
      }
    };
    loadLogo();
    
    // Initial: show mini bar for 3 seconds, then expand with glow effect
    // Mini bar starts visible, then expands after 3s, stays expanded for 60s
    const initialExpandTimer = setTimeout(() => {
      setBarExpanded(true);
      setBarGlowing(true);
      const collapseTimer = setTimeout(() => {
        setBarExpanded(false);
        setBarGlowing(false);
      }, 60000);
      autoCollapseTimerRef.current = collapseTimer;
    }, 3000); // Wait 3 seconds before expanding
    
    return () => { 
      mountedRef.current = false; 
      cleanup();
      clearTimeout(initialExpandTimer);
      if (autoCollapseTimerRef.current) clearTimeout(autoCollapseTimerRef.current);
    }; 
  }, []);
  
  // Close export menu when clicking outside
  useEffect(() => {
    if (!showExportMenu) return;
    const handleClickOutside = () => setShowExportMenu(false);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showExportMenu]);

  const setState = useCallback((s, text) => {
    stateRef.current = s;
    if (mountedRef.current) {
      setAgentState(s);
      if (text !== undefined) setStatusText(text);
    }
  }, []);

  // Idle timeout: DISABLED - W IA only closes manually
  // User requested to remove auto-close feature
  const resetIdleTimer = useCallback(() => {
    // Timers disabled - W IA stays open until user closes manually
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idlePhaseRef.current = 'active';
    // No automatic timeout - user controls when to close
  }, []);

  const cleanup = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    stopTTSPlayback();
    clearSilenceDetection();
    if (mediaRecorderRef.current?.state === 'recording') {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(t => t.stop());
      audioStreamRef.current = null;
    }
    if (audioContextRef.current?.state !== 'closed') {
      try { audioContextRef.current.close(); } catch (e) {}
      audioContextRef.current = null;
    }
    setAudioStream(null);
  };

  const stopTTSPlayback = () => {
    if (ttsAudioRef.current) { ttsAudioRef.current.pause(); ttsAudioRef.current = null; }
    if (ttsSourceRef.current) { try { ttsSourceRef.current.stop(); } catch (e) {} ttsSourceRef.current = null; }
  };

  const clearSilenceDetection = () => {
    if (silenceCheckRef.current) { clearInterval(silenceCheckRef.current); silenceCheckRef.current = null; }
  };

  const ttsAnalyserRef = useRef(null);
  const ttsAnimationRef = useRef(null);

  const playAudioBase64 = (base64Audio) => {
    return new Promise(async (resolve) => {
      try {
        const bin = atob(base64Audio);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const ctx = audioContextRef.current;
        if (ctx && ctx.state !== 'closed') {
          try {
            if (ctx.state === 'suspended') await ctx.resume();
            const buf = await ctx.decodeAudioData(bytes.buffer.slice(0));
            const src = ctx.createBufferSource();
            src.buffer = buf;
            
            // Criar analyser para o Orb reagir à voz
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            ttsAnalyserRef.current = analyser;
            
            // Criar compressor para volume estável sem oscilação
            const compressor = ctx.createDynamicsCompressor();
            compressor.threshold.setValueAtTime(-24, ctx.currentTime);
            compressor.knee.setValueAtTime(30, ctx.currentTime);
            compressor.ratio.setValueAtTime(12, ctx.currentTime);
            compressor.attack.setValueAtTime(0.003, ctx.currentTime);
            compressor.release.setValueAtTime(0.25, ctx.currentTime);
            
            // Gain alto e constante
            const gain = ctx.createGain();
            gain.gain.value = 3.5;  // Volume bem alto
            
            // Conectar: src -> analyser -> compressor -> gain -> destination
            src.connect(analyser);
            analyser.connect(compressor);
            compressor.connect(gain);
            gain.connect(ctx.destination);
            
            // Animação do analyser para o Orb
            const updateTtsAnalyser = () => {
              if (!ttsAnalyserRef.current) return;
              const dataArray = new Uint8Array(ttsAnalyserRef.current.frequencyBinCount);
              ttsAnalyserRef.current.getByteFrequencyData(dataArray);
              
              // Calcular grave (baixas frequências) e agudo (altas frequências)
              const bassEnd = Math.floor(dataArray.length * 0.3);
              const trebleStart = Math.floor(dataArray.length * 0.6);
              
              let bassSum = 0, trebleSum = 0;
              for (let i = 0; i < bassEnd; i++) bassSum += dataArray[i];
              for (let i = trebleStart; i < dataArray.length; i++) trebleSum += dataArray[i];
              
              const grave = bassSum / (bassEnd * 255);
              const agudo = trebleSum / ((dataArray.length - trebleStart) * 255);
              
              setTtsAnalyserData({ grave, agudo });
              ttsAnimationRef.current = requestAnimationFrame(updateTtsAnalyser);
            };
            updateTtsAnalyser();
            
            ttsSourceRef.current = src;
            src.onended = () => { 
              ttsSourceRef.current = null; 
              ttsAnalyserRef.current = null;
              if (ttsAnimationRef.current) {
                cancelAnimationFrame(ttsAnimationRef.current);
                ttsAnimationRef.current = null;
              }
              setTtsAnalyserData({ grave: 0, agudo: 0 });
              resolve(); 
            };
            src.start();
            console.log('[WIA] Audio started via AudioContext with compressor');
            return;
          } catch (e) {}
        }
        // Fallback: Blob URL
        const blob = new Blob([bytes], { type: 'audio/mpeg' });
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.volume = 1.0;  // Máximo para Audio element
        ttsAudioRef.current = audio;
        audio.onended = () => { URL.revokeObjectURL(url); ttsAudioRef.current = null; resolve(); };
        audio.onerror = (e) => { console.error('[WIA] Audio error:', e); URL.revokeObjectURL(url); ttsAudioRef.current = null; resolve(); };
        console.log('[WIA] Playing audio via fallback Audio element');
        audio.play().catch((e) => { console.error('[WIA] Audio play error:', e); resolve(); });
      } catch (e) { resolve(); }
    });
  };

  const initialize = async () => {
    setState('starting', 'Permitir microfone...');
    
    // iOS Safari requires getUserMedia to be called directly from user gesture
    // Request mic FIRST before anything else
    let micGranted = false;
    try {
      console.log('[WIA] Solicitando permissão do microfone...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { 
          echoCancellation: true, 
          noiseSuppression: true, 
          autoGainControl: true,
          // Constraints adicionais para reduzir ruído
          channelCount: 1,
          sampleRate: 16000,
          sampleSize: 16
        }
      });
      audioStreamRef.current = stream;
      setAudioStream(stream);
      micReadyRef.current = true;
      micGranted = true;
      console.log('[WIA] Microfone ativado com sucesso');
      
      // Setup audio context AFTER mic permission
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = ctx;
      if (ctx.state === 'suspended') await ctx.resume();
      
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      ctx.createMediaStreamSource(stream).connect(analyser);
      analyserRef.current = analyser;
    } catch (err) {
      console.error('[WIA] Erro ao acessar microfone:', err.name, err.message);
      setShowTextInput(true);
      micReadyRef.current = false;
      
      // Create audio context anyway for TTS
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = ctx;
      if (ctx.state === 'suspended') await ctx.resume();
    }

    const name = memberRef.current?.name?.split(' ')[0] || '';
    // UI text shows "W" but TTS text uses "Dáblio" for correct Portuguese pronunciation
    // "WClub" pronunciado como "Dáblio Club" para soar natural
    const welcomeUI = micGranted 
      ? `Olá ${name}, sou W, seu consultor exclusivo do WClub. Me conte o que deseja.`
      : `Olá ${name}, sou W. Não consegui acessar seu microfone. Use o teclado para conversar comigo.`;
    const welcomeTTS = micGranted 
      ? `Olá ${name}, sou Dáblio, seu consultor exclusivo do Dáblio Club. Me conte o que deseja.`
      : `Olá ${name}, sou Dáblio. Não consegui acessar seu microfone. Use o teclado para conversar comigo.`;
    setMessages([{ role: 'assistant', content: welcomeUI }]);

    // Play welcome and auto-start listening (use TTS version with "Dáblio" pronunciation)
    try {
      setState('speaking', '');
      // Use different cache key based on mic status - v10 to force re-cache with "Dáblio Club"
      const cacheKey = micGranted 
        ? `wclub_welcome_v10_${name}` 
        : `wclub_welcome_nomic_v10_${name}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        await playAudioBase64(cached);
      } else {
        const r = await axios.post(`${API}/voice/speak`, { text: welcomeTTS });
        if (r.data?.audio) {
          try { localStorage.setItem(cacheKey, r.data.audio); } catch (e) {}
          await playAudioBase64(r.data.audio);
        }
      }
    } catch (e) {
      console.error('[WIA] Erro no TTS welcome:', e);
    }

    if (micReadyRef.current) {
      setState('idle', '');
      resetIdleTimer();
      setTimeout(() => {
        if (stateRef.current === 'idle' && micReadyRef.current && mountedRef.current) startRecording();
      }, 500);
    } else {
      setState('idle', 'Use o teclado');
      setShowTextInput(true);
    }
  };

  // Mic control functions for tap/hold interaction
  const handleMicTap = () => {
    // If long press was triggered, don't process tap
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      return;
    }

    const s = stateRef.current;
    
    // If W is speaking, stop it
    if (s === 'speaking' || isSpeakingRef.current) {
      stopTTSPlayback();
      setState('idle', '');
      if (micReadyRef.current) {
        setTimeout(() => startRecording(), 100);
      }
      return;
    }
    
    // If ready/idle - start listening
    if (s === 'ready') {
      initialize();
      return;
    }
    
    // If idle and paused - resume
    if (isPaused) {
      setIsPaused(false);
      if (micReadyRef.current) startRecording();
      return;
    }
    
    // If listening - pause
    if (s === 'listening' && isRecordingRef.current) {
      setIsPaused(true);
      if (mediaRecorderRef.current?.state === 'recording') {
        try { mediaRecorderRef.current.pause(); } catch (e) {}
      }
      setState('idle', 'Pausado');
      return;
    }
    
    // If idle - start recording
    if (s === 'idle' && micReadyRef.current) {
      resetIdleTimer();
      startRecording();
      return;
    }
  };

  const handleMicLongPressStart = () => {
    longPressTriggeredRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      // Full shutdown on long press
      handleFullShutdown();
    }, 800); // 800ms for long press
  };

  const handleMicLongPressEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // Expand bar and start glow effect
  const expandBar = () => {
    // Cancel auto-collapse
    if (autoCollapseTimerRef.current) {
      clearTimeout(autoCollapseTimerRef.current);
      autoCollapseTimerRef.current = null;
    }
    setBarExpanded(true);
    setBarGlowing(true);
    
    // Start auto-collapse timer (60s inactivity - enough to see all rotating texts)
    autoCollapseTimerRef.current = setTimeout(() => {
      setBarExpanded(false);
      setBarGlowing(false);
    }, 60000);
  };
  
  // Keep bar active (reset auto-collapse timer)
  const keepBarActive = () => {
    if (autoCollapseTimerRef.current) {
      clearTimeout(autoCollapseTimerRef.current);
    }
    setBarGlowing(true);
    autoCollapseTimerRef.current = setTimeout(() => {
      setBarExpanded(false);
      setBarGlowing(false);
    }, 60000);
  };

  // Collapse bar (manual or after send)
  const collapseBar = () => {
    if (autoCollapseTimerRef.current) {
      clearTimeout(autoCollapseTimerRef.current);
      autoCollapseTimerRef.current = null;
    }
    setBarExpanded(false);
    setBarGlowing(false);
  };

  const handleFullShutdown = () => {
    // Stop everything
    stopTTSPlayback();
    if (isRecordingRef.current && mediaRecorderRef.current?.state === 'recording') {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
      isRecordingRef.current = false;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(t => t.stop());
      audioStreamRef.current = null;
    }
    setAudioStream(null);
    setIsPaused(false);
    setState('ready', 'Toque no orb');
    
    // Visual feedback - could add a toast here
    console.log('[WIA] Full shutdown via long press');
  };

  const handleOrbTap = async () => {
    const s = stateRef.current;
    if (s === 'ready') { await initialize(); }
    else if (s === 'idle' && micReadyRef.current) { resetIdleTimer(); startRecording(); }
    else if (s === 'listening' && isRecordingRef.current) { stopRecording(); }
    else if (s === 'speaking') { stopTTSPlayback(); if (micReadyRef.current) startRecording(); }
  };

  const startSilenceDetection = () => {
    clearSilenceDetection();
    const a = analyserRef.current;
    if (!a) return;
    const d = new Uint8Array(a.frequencyBinCount);
    let sf = 0;
    let hasSpoken = false;
    silenceCheckRef.current = setInterval(() => {
      if (!isRecordingRef.current) { clearSilenceDetection(); return; }
      a.getByteFrequencyData(d);
      const avg = d.reduce((x, y) => x + y, 0) / d.length;
      if (avg > 20) { hasSpoken = true; sf = 0; }
      else { sf++; if (hasSpoken && sf >= SILENCE_FRAMES) { clearSilenceDetection(); stopRecording(); } }
    }, 50);
  };

  const startRecording = () => {
    // BLOQUEIO: Não gravar se W estiver falando
    if (isSpeakingRef.current) {
      console.log('[WIA] Skipping recording - W is speaking');
      return;
    }
    // BLOQUEIO: Não gravar se esperando foto
    if (waitingForAttachmentRef.current) {
      console.log('[WIA] Skipping recording - waiting for photo attachment');
      return;
    }
    // Não gravar se TTS ainda estiver tocando
    if (ttsSourceRef.current || ttsAudioRef.current) {
      console.log('[WIA] Skipping recording - TTS still playing');
      return;
    }
    // Não gravar se estado não for idle
    if (stateRef.current === 'speaking' || stateRef.current === 'processing') {
      console.log('[WIA] Skipping recording - state is', stateRef.current);
      return;
    }
    if (!audioStreamRef.current || isRecordingRef.current) return;
    try {
      const rec = new MediaRecorder(audioStreamRef.current);
      mediaRecorderRef.current = rec;
      audioChunksRef.current = [];
      isRecordingRef.current = true;
      rec.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      rec.onstop = () => {
        isRecordingRef.current = false;
        const blob = new Blob(audioChunksRef.current, { type: rec.mimeType || 'audio/webm' });
        // Não processar se W começou a falar durante a gravação
        if (isSpeakingRef.current) {
          console.log('[WIA] Discarding recording - W started speaking');
          setState('idle', ''); 
          resetIdleTimer();
          return;
        }
        if (blob.size > 1000) processVoice(blob);
        else { setState('idle', ''); resetIdleTimer(); }
      };
      rec.start();
      setState('listening', 'Ouvindo...');
      startSilenceDetection();
    } catch (err) { isRecordingRef.current = false; setState('idle', ''); }
  };

  const stopRecording = () => {
    // Don't process voice if waiting for photo attachment
    if (waitingForAttachmentRef.current) {
      console.log('[WIA] Skipping voice process - waiting for attachment');
      if (mediaRecorderRef.current?.state === 'recording') {
        try { mediaRecorderRef.current.stop(); } catch (e) {}
      }
      clearSilenceDetection();
      setState('idle', '');
      return;
    }
    clearSilenceDetection();
    if (mediaRecorderRef.current?.state === 'recording') {
      setState('processing', 'Processando...');
      mediaRecorderRef.current.stop();
    }
  };

  const processVoice = async (audioBlob) => {
    try {
      const fd = new FormData();
      fd.append('file', audioBlob, 'audio.webm');
      const r = await axios.post(`${API}/voice/transcribe`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const text = r.data?.text?.trim();
      if (!text) { setState('idle', ''); resetIdleTimer(); return; }

      // FILTRO: Ignorar transcrições de ruído/eco conhecidas
      const lowerText = text.toLowerCase().trim();
      
      // Filtro mais agressivo de ruído - transcrições curtas de ruído ambiente
      const noisePatterns = [
        'amara', 'legendas', 'comunidade', 'subtitles', 'subscribe', 'inscreva',
        'música', 'music', '[música]', '[music]', '♪', '🎵',
        'hum', 'hmm', 'uhm', 'uh', 'ah', 'eh', 'ih', 'oh',
        'aplicação', 'transcrição', 'áudio', 'som', 'ruído'
      ];
      const isNoise = noisePatterns.some(pattern => lowerText.includes(pattern));
      
      // Transcrições muito curtas (menos de 3 caracteres) ou só pontuação são ruído
      const cleanText = text.replace(/[.,!?;:\s]/g, '');
      const isTooShort = cleanText.length < 3;
      
      if (isNoise || isTooShort) {
        console.log('[WIA] Ignored noise/short transcription:', text);
        setState('idle', '');
        resetIdleTimer();
        return;
      }

      // Check for PNG generation command
      const pngPatterns = ['gere png', 'gerar png', 'gera png', 'gere o png', 'gerar o png', 'gera o png', 'png da resposta', 'compartilhar resposta', 'compartilhe a resposta'];
      const wantsPNG = pngPatterns.some(pattern => lowerText.includes(pattern));
      if (wantsPNG) {
        await handleGeneratePNG(text);
        return;
      }

      // Check for EXPLICIT shutdown commands ONLY - "Ok W desligar", "Ok W obrigado"
      // Simple phrases like "ok", "pronto", "valeu" are NOT shutdown commands - they are sent to AI
      const shutdownPatterns = ['ok w desligar', 'ok w obrigado', 'w desligar', 'w obrigado', 'desligar w', 'pode desligar', 'encerrar w', 'finalizar w'];
      const wantsShutdown = shutdownPatterns.some(pattern => lowerText.includes(pattern));
      if (wantsShutdown) {
        await handleShutdown(text);
        return;
      }

      // ALL other messages go to AI - no auto-shutdown from simple phrases
      await sendToAI(text);
    } catch (err) { setState('idle', ''); resetIdleTimer(); }
  };

  // Handle "Gere PNG da resposta" command
  const handleGeneratePNG = async (userText) => {
    // Add user message
    if (userText) setMessages(prev => [...prev, { role: 'user', content: userText }]);
    
    // Check if we have a previous assistant message to generate PNG from
    const assistantMessages = messagesRef.current.filter(m => m.role === 'assistant');
    if (assistantMessages.length === 0) {
      const noResponse = "Ainda não tenho uma resposta para gerar o PNG. Faça uma pergunta primeiro.";
      setMessages(prev => [...prev, { role: 'assistant', content: noResponse }]);
      await playTTS(noResponse);
      return;
    }
    
    // Get last assistant message and its question
    const lastMsgIndex = messagesRef.current.length - 1;
    const lastResponse = assistantMessages[assistantMessages.length - 1].content;
    const questionIndex = messagesRef.current.findIndex(m => m.content === lastResponse) - 1;
    const question = questionIndex >= 0 && messagesRef.current[questionIndex]?.role === 'user'
      ? messagesRef.current[questionIndex].content
      : '';
    
    // Set the response for PNG generation
    // Generate verification hash
    const raw = `${lastResponse}|${Date.now()}|wclub-wia-response`;
    const encoder = new TextEncoder();
    const data = encoder.encode(raw);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const fullHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    const shortHash = fullHash.substring(0, 12).toUpperCase();
    
    // Generate QR code
    let qrDataUrl = '';
    try {
      qrDataUrl = await QRCode.toDataURL(`${window.location.origin}/verify/${shortHash}`, {
        width: 120, margin: 1, color: { dark: '#ffffff', light: '#00000000' }, errorCorrectionLevel: 'M'
      });
    } catch (e) { console.error('[QR]', e); }
    
    // Save verification
    try {
      await axios.post(`${API}/w-verify/save`, {
        hash: fullHash, percentage: null, member_id: memberRef.current?.id,
        member_name: memberRef.current?.name, date: new Date().toISOString()
      });
    } catch (e) {}
    
    setResponseForPNG({
      conversations: [{ question, response: lastResponse }],
      timestamp: new Date().toISOString(),
      hash: shortHash,
      qrDataUrl
    });
    setShowResponsePNGCard(true);
    
    const confirmation = "Pronto! Gerando o PNG da minha última resposta. Você pode compartilhar agora.";
    setMessages(prev => [...prev, { role: 'assistant', content: confirmation }]);
    await playTTS(confirmation);
  };

  // Generate PNG of analyzed image with authenticity percentage overlay
  // Generate SHA-256 hash for authenticity verification
  const generateAuthHash = async (imageUrl, percentage) => {
    const raw = `${imageUrl}|${percentage}|${Date.now()}|wclub-wia`;
    const encoder = new TextEncoder();
    const data = encoder.encode(raw);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const generateImageWithPercentage = async (imageUrl, percentage) => {
    // Generate unique hash
    const fullHash = await generateAuthHash(imageUrl, percentage);
    const shortHash = fullHash.substring(0, 12).toUpperCase();
    const now = new Date();
    const dateStr = `${String(now.getDate()).padStart(2,'0')}/${String(now.getMonth()+1).padStart(2,'0')}/${now.getFullYear()}`;

    // Save verification to backend
    try {
      await axios.post(`${API}/w-verify/save`, {
        hash: fullHash,
        percentage,
        image_url: imageUrl,
        member_id: memberRef.current?.id,
        member_name: memberRef.current?.name,
        date: now.toISOString()
      });
    } catch (e) { console.error('[W Verify] Save error:', e); }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = async () => {
        try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        // Badge dimensions
        const badgeSize = Math.min(img.width, img.height) * 0.25;
        const margin = Math.min(img.width, img.height) * 0.08;
        const badgeX = img.width - badgeSize - margin;
        const badgeY = img.height - badgeSize - margin - badgeSize * 0.35;
        const cx = badgeX + badgeSize / 2;
        const cy = badgeY + badgeSize / 2;
        const innerR = badgeSize / 2;
        const arcR = innerR - 6;
        const ringR = innerR + badgeSize * 0.18; // Outer ring for text
        
        // Shadow
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, ringR + 6, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
        ctx.fill();
        
        // Outer text ring background
        ctx.beginPath();
        ctx.arc(cx, cy, ringR + 2, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
        ctx.fill();

        // Inner badge circle
        ctx.beginPath();
        ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, innerR);
        grad.addColorStop(0, '#12161e');
        grad.addColorStop(1, '#0a0d10');
        ctx.fillStyle = grad;
        ctx.fill();
        
        // Thin ring border
        ctx.beginPath();
        ctx.arc(cx, cy, innerR + 1, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(59,130,246,0.15)';
        ctx.lineWidth = 1;
        ctx.stroke();
        
        // Percentage arc
        ctx.beginPath();
        ctx.arc(cx, cy, arcR, -Math.PI / 2, -Math.PI / 2 + (percentage / 100) * Math.PI * 2);
        ctx.strokeStyle = percentage >= 80 ? '#3b82f6' : percentage >= 50 ? '#64748b' : '#ef4444';
        ctx.lineWidth = badgeSize * 0.07;
        ctx.lineCap = 'round';
        ctx.stroke();
        
        // W logo
        ctx.font = `bold ${badgeSize * 0.38}px Jost, sans-serif`;
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('W', cx, cy - badgeSize * 0.20);
        
        // "AUTHENTIC" label between W and %
        ctx.font = `600 ${badgeSize * 0.10}px Jost, sans-serif`;
        ctx.fillStyle = 'rgba(255,255,255,0.45)';
        ctx.letterSpacing = '2px';
        ctx.fillText('AUTHENTIC', cx, cy + badgeSize * 0.02);
        
        // Percentage bold
        ctx.font = `bold ${badgeSize * 0.22}px Jost, sans-serif`;
        ctx.fillStyle = percentage >= 80 ? '#60a5fa' : percentage >= 50 ? '#94a3b8' : '#f87171';
        ctx.fillText(`${percentage}%`, cx, cy + badgeSize * 0.20);
        
        // === FULL CIRCLE TEXT RING (uniform) ===
        const ringText = `VERIFIED BY W IA - WCLUB \u2022 #${shortHash} \u2022 ${dateStr} \u2022 `;
        const fontSize = badgeSize * 0.085;
        ctx.font = `bold ${fontSize}px Jost, sans-serif`;
        ctx.fillStyle = 'rgba(255,255,255,0.50)';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        let singleWidth = 0;
        for (let i = 0; i < ringText.length; i++) {
          singleWidth += ctx.measureText(ringText[i]).width;
        }
        const circumference = 2 * Math.PI * ringR;
        const repeats = Math.ceil(circumference / singleWidth);
        const fullText = ringText.repeat(repeats);
        
        let angle = -Math.PI / 2;
        for (let i = 0; i < fullText.length; i++) {
          const ch = fullText[i];
          const charW = ctx.measureText(ch).width;
          const charAngle = charW / ringR;
          const midAngle = angle + charAngle / 2;
          
          if (angle > -Math.PI / 2 + Math.PI * 2) break;
          
          ctx.save();
          ctx.translate(cx + ringR * Math.cos(midAngle), cy + ringR * Math.sin(midAngle));
          ctx.rotate(midAngle + Math.PI / 2);
          ctx.fillText(ch, 0, 0);
          ctx.restore();
          
          angle += charAngle;
        }
        
        // === QR CODE REAL - bottom-right diagonal ===
        const qrSize = badgeSize * 0.24;
        const qrX = cx + ringR * 0.62;
        const qrY = cy + ringR * 0.62;
        
        const verifyUrl = `${window.location.origin}/verify/${shortHash}`;
        try {
          const qrDataUrl = await QRCode.toDataURL(verifyUrl, {
            width: Math.round(qrSize * 3),
            margin: 0,
            color: { dark: '#ffffff', light: '#00000000' },
            errorCorrectionLevel: 'M'
          });
          const qrImg = new Image();
          await new Promise((res, rej) => { qrImg.onload = res; qrImg.onerror = rej; qrImg.src = qrDataUrl; });
          
          // QR background
          const qrPad = 4;
          ctx.fillStyle = 'rgba(0,0,0,0.70)';
          ctx.beginPath();
          ctx.roundRect(qrX - qrPad, qrY - qrPad, qrSize + qrPad * 2, qrSize + qrPad * 2, 4);
          ctx.fill();
          ctx.strokeStyle = 'rgba(59,130,246,0.15)';
          ctx.lineWidth = 0.5;
          ctx.stroke();
          
          // Draw real QR
          ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
        } catch (qrErr) {
          console.error('[QR Gen]', qrErr);
        }
        
        ctx.restore();
        resolve(canvas.toDataURL('image/png', 1.0));
        } catch (canvasErr) { reject(canvasErr); }
      };
      img.onerror = reject;
      img.src = imageUrl;
    });
  };

  // Handle shutdown commands - "Ok W desligar", "Ok W obrigado"
  const handleShutdown = async (userText) => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idlePhaseRef.current = 'done';
    const name = memberRef.current?.name?.split(' ')[0] || '';
    if (userText) setMessages(prev => [...prev, { role: 'user', content: userText }]);
    
    // Choose farewell based on command
    const lowerText = userText.toLowerCase();
    let farewell;
    if (lowerText.includes('obrigado')) {
      farewell = `Por nada, ${name}! Foi um prazer ajudar. Até a próxima!`;
    } else {
      farewell = `Desligando, ${name}. Quando precisar, é só chamar!`;
    }
    
    setMessages(prev => [...prev, { role: 'assistant', content: farewell }]);
    setState('speaking', '');
    try {
      const r = await axios.post(`${API}/voice/speak`, { text: farewell });
      if (r.data?.audio) await playAudioBase64(r.data.audio);
    } catch (e) {}
    
    // Full shutdown
    endConversation();
    setIsPaused(false);
    setState('ready', 'Toque no orb');
  };

  // Handle simple end commands - "ok", "pronto", "valeu", etc
  const handleSimpleEnd = async (userText) => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idlePhaseRef.current = 'done';
    const name = memberRef.current?.name?.split(' ')[0] || '';
    if (userText) setMessages(prev => [...prev, { role: 'user', content: userText }]);
    
    // Friendly acknowledgment
    const responses = [
      `Perfeito, ${name}! Se precisar de mais alguma coisa, é só falar.`,
      `Beleza! Fico à disposição, ${name}.`,
      `Ok! Qualquer dúvida, estou aqui.`,
      `Certo! Quando precisar, é só chamar.`
    ];
    const farewell = responses[Math.floor(Math.random() * responses.length)];
    
    setMessages(prev => [...prev, { role: 'assistant', content: farewell }]);
    setState('speaking', '');
    try {
      const r = await axios.post(`${API}/voice/speak`, { text: farewell });
      if (r.data?.audio) await playAudioBase64(r.data.audio);
    } catch (e) {}
    
    // Shutdown
    endConversation();
    setIsPaused(false);
    setState('ready', 'Toque no orb');
  };

  const handleGoodbye = async (userText) => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idlePhaseRef.current = 'done';
    const name = memberRef.current?.name?.split(' ')[0] || '';
    if (userText) setMessages(prev => [...prev, { role: 'user', content: userText }]);
    const goodbye = `Estou sempre à disposição, ${name}. É só chamar!`;
    setMessages(prev => [...prev, { role: 'assistant', content: goodbye }]);
    setState('speaking', '');
    try {
      const r = await axios.post(`${API}/voice/speak`, { text: goodbye });
      if (r.data?.audio) await playAudioBase64(r.data.audio);
    } catch (e) {}
    endConversation();
  };

  const endConversation = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    stopTTSPlayback();
    clearSilenceDetection();
    if (isRecordingRef.current && mediaRecorderRef.current?.state === 'recording') {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
      isRecordingRef.current = false;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(t => t.stop());
      audioStreamRef.current = null;
    }
    setAudioStream(null);
    micReadyRef.current = false;
    setAttachedImages([]);
    setState('ready', 'Toque no orb');
  };

  // Handle file attachment
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Filter valid images
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) return false;
      if (file.size > 5 * 1024 * 1024) return false; // max 5MB each
      return true;
    });
    
    if (validFiles.length === 0) {
      alert('Selecione apenas imagens (máximo 5MB cada)');
      return;
    }
    
    // Limit to 5 images total
    const maxImages = 5;
    const currentCount = attachedImages.length;
    const toAdd = validFiles.slice(0, maxImages - currentCount);
    
    if (toAdd.length === 0) {
      alert('Máximo de 5 imagens permitido');
      return;
    }
    
    toAdd.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result.split(',')[1];
        setAttachedImages(prev => [...prev, {
          base64,
          preview: event.target.result,
          name: file.name,
          type: file.type
        }]);
        // Image attached - no longer waiting
        setWaitingForAttachment(false);
      };
      reader.readAsDataURL(file);
    });
    
    setShowTextInput(true);
    
    // Reset input
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachedImages(prev => prev.filter((_, i) => i !== index));
  };

  const clearAllAttachments = () => {
    setAttachedImages([]);
  };

  const sendToAI = async (text) => {
    const m = memberRef.current;
    if (!m) return;
    
    // Capture images from ref to ensure we have the latest value
    const currentImages = attachedImagesRef.current;
    console.log('[WIA] sendToAI called, numImages:', currentImages.length, 'text:', text);
    
    // Add user message with images if attached
    const userMsg = { role: 'user', content: text };
    if (currentImages.length > 0) {
      userMsg.images = currentImages.map(img => img.preview);
      userMsg.image = currentImages[0].preview; // For backwards compatibility in chat display
    }
    setMessages(prev => [...prev, userMsg]);
    
    setState('processing', 'Pensando...');
    resetIdleTimer();
    
    // Clear attachments immediately
    setAttachedImages([]);
    
    try {
      // Include images in request if attached
      const payload = { 
        member_id: m.id, 
        message: text, 
        context: {},
        geolocation: geoRef.current || null,
        local_time: new Date().toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo' }),
        is_first_message: messagesRef.current.length <= 1
      };
      if (currentImages.length > 0) {
        // Send multiple images
        payload.images = currentImages.map(img => ({
          base64: img.base64,
          type: img.type
        }));
        // For backwards compatibility, also send first image
        payload.image_base64 = currentImages[0].base64;
        payload.image_type = currentImages[0].type;
        console.log('[WIA] Sending with', currentImages.length, 'images');
      }
      
      console.log('[WIA] Calling askSommelier...');
      const r = await askSommelier(payload);
      const aiText = r.data.response;
      console.log('[WIA] Got response:', aiText?.substring(0, 100));
      
      // Mostrar texto imediatamente
      setMessages(prev => [...prev, { role: 'assistant', content: aiText }]);
      
      // Check if response contains authenticity analysis
      const authMatch = aiText.match(/(\d+)%\s*(autêntico|autentico|original|genuíno|genuino)/i);
      if (authMatch && currentImages.length > 0) {
        const percentage = parseInt(authMatch[1]);
        console.log('[WIA] Authenticity detected:', percentage, '%');
        
        // Generate verification hash + QR
        let authHash = '', authQrUrl = '';
        try {
          const raw = `${aiText}|${percentage}|${Date.now()}|wclub-auth`;
          const enc = new TextEncoder();
          const buf = await crypto.subtle.digest('SHA-256', enc.encode(raw));
          authHash = Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 12).toUpperCase();
          authQrUrl = await QRCode.toDataURL(`${window.location.origin}/verify/${authHash}`, {
            width: 120, margin: 1, color: { dark: '#ffffff', light: '#00000000' }, errorCorrectionLevel: 'M'
          });
          // Save to backend
          await axios.post(`${API}/w-verify/save`, {
            hash: Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join(''),
            percentage, member_id: memberRef.current?.id, member_name: memberRef.current?.name, date: new Date().toISOString()
          });
        } catch (e) { console.error('[Auth Hash]', e); }
        
        setAuthenticityResult({
          text: aiText,
          percentage,
          productName: aiText.split('\n')[0].replace(/[-–—]/g, '').trim(),
          images: currentImages.map(img => img.preview),
          hash: authHash,
          qrDataUrl: authQrUrl
        });
      }
      
      // TTS - SEMPRE tocar o áudio
      console.log('[WIA] Starting TTS...');
      await playTTS(aiText);
    } catch (err) {
      console.error('[WIA] sendToAI Error:', err);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Desculpe, ocorreu um erro.' }]);
      setState('idle', '');
      resetIdleTimer();
    }
  };

  const playTTS = async (text) => {
    // BLOQUEAR gravação enquanto fala
    isSpeakingRef.current = true;
    
    // PARAR qualquer gravação antes de falar
    if (isRecordingRef.current && mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      isRecordingRef.current = false;
    }
    clearSilenceDetection();
    
    // DESLIGAR MICROFONE completamente para não captar eco
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => {
        track.enabled = false;
      });
      console.log('[WIA] Microphone MUTED');
    }
    
    setState('speaking', '');
    console.log('[WIA] playTTS iniciando, texto:', text?.substring(0, 50));
    try {
      const r = await axios.post(`${API}/voice/speak`, { text });
      console.log('[WIA] TTS response received, has audio:', !!r.data?.audio);
      if (!r.data?.audio) { 
        console.log('[WIA] No audio in response');
        // Religar microfone
        if (audioStreamRef.current) {
          audioStreamRef.current.getTracks().forEach(track => { track.enabled = true; });
        }
        isSpeakingRef.current = false;
        autoResume(); 
        return; 
      }
      await playAudioBase64(r.data.audio);
      console.log('[WIA] Audio playback finished');
      // Esperar para eco dissipar
      await new Promise(r => setTimeout(r, 2500));
      
      // RELIGAR MICROFONE
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => {
          track.enabled = true;
        });
        console.log('[WIA] Microphone UNMUTED');
      }
      
      isSpeakingRef.current = false;
      autoResume();
    } catch (err) { 
      console.error('[WIA] playTTS error:', err);
      // Religar microfone em caso de erro
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => { track.enabled = true; });
      }
      isSpeakingRef.current = false;
      autoResume(); 
    }
  };

  const autoResume = () => {
    if (micReadyRef.current && mountedRef.current) {
      setState('idle', '');
      resetIdleTimer();
      // Aguardar mais tempo para evitar capturar eco do TTS
      setTimeout(() => {
        // Só iniciar gravação se ainda estiver idle (não speaking)
        if (stateRef.current === 'idle' && micReadyRef.current && mountedRef.current) {
          startRecording();
        }
      }, 1500); // 1.5 segundos para eco dissipar
    } else { setState('idle', ''); resetIdleTimer(); }
  };

  const handleTextSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const text = input;
    setInput('');
    setShowTextInput(false);
    collapseBar(); // Collapse bar and remove glow when sending message
    
    if (stateRef.current === 'ready') {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = ctx;
      if (ctx.state === 'suspended') await ctx.resume();
      setState('idle', '');
    }
    stopTTSPlayback();
    clearSilenceDetection();
    if (isRecordingRef.current && mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      isRecordingRef.current = false;
    }
    // Check for explicit shutdown only
    const lowerText = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const shutdownPatterns = ['ok w desligar', 'ok w obrigado', 'w desligar', 'w obrigado', 'desligar w', 'pode desligar', 'encerrar w', 'finalizar w'];
    if (shutdownPatterns.some(p => lowerText.includes(p))) {
      await handleShutdown(text);
      return;
    }
    await sendToAI(text);
  };

  if (!currentMember) {
    return (
      <div className="flex items-center justify-center h-[70vh]" data-testid="wia-no-member">
        <p className="text-white/20 text-xs font-['Jost']">Selecione um membro na aba Cartoes</p>
      </div>
    );
  }

  const isListening = agentState === 'listening';
  const isSpeaking = agentState === 'speaking';
  const isProcessing = agentState === 'processing';
  const isReady = agentState === 'ready';

  return (
    <div className="flex flex-col bg-black h-full" style={{ minHeight: '100dvh', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} data-testid="wia-container">
      <div className="flex-1 relative bg-black overflow-hidden min-h-0 cursor-pointer"
        onClick={handleOrbTap} data-testid="wia-orb-area" style={{ touchAction: 'manipulation' }}>
        <OrbVisualizer isActive={!isReady} mediaStream={audioStream} isSpeaking={isSpeaking} />

        {isReady && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <motion.p animate={{ opacity: [0.15, 0.35, 0.15] }} transition={{ duration: 3, repeat: Infinity }}
              className="text-white/25 text-xs font-['Jost'] font-light tracking-wider">Toque para iniciar</motion.p>
          </div>
        )}

        {isListening && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-5">
            <motion.div animate={{ scale: [1, 1.4, 1], opacity: [0.12, 0, 0.12] }}
              transition={{ duration: 1.5, repeat: Infinity }} className="w-36 h-36 rounded-full border border-red-500/15" />
          </div>
        )}

        {isSpeaking && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-5">
            <motion.div animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.05, 0.2] }}
              transition={{ duration: 1, repeat: Infinity }} className="w-40 h-40 rounded-full border-2 border-blue-400/20" />
            <motion.div animate={{ scale: [1.1, 1.5, 1.1], opacity: [0.1, 0, 0.1] }}
              transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }} className="absolute w-48 h-48 rounded-full border border-blue-300/10" />
          </div>
        )}

        {!isReady && (
          <div className="absolute bottom-4 left-0 right-0 flex flex-col items-center pointer-events-none z-10 gap-2">
            {/* Curiosidade do luxo enquanto processa */}
            {isProcessing && curiosity && (
              <motion.div 
                key={curiosity}
                initial={{ opacity: 0, y: 8 }} 
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.5 }}
                className="px-4 py-2 rounded-2xl bg-black/50 backdrop-blur-md border border-white/[0.06] max-w-[85%]"
              >
                <p className="text-white/25 text-[9px] font-['Jost'] font-light text-center italic leading-relaxed">{curiosity}</p>
              </motion.div>
            )}
            <motion.div key={agentState} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-black/40 backdrop-blur-md border border-white/[0.04]">
              {agentState === 'starting' && <div className="flex gap-1">{[0,1,2].map(i => <motion.span key={i} className="w-1 h-1 rounded-full bg-white/25" animate={{ opacity: [0.2, 0.8, 0.2] }} transition={{ duration: 1, repeat: Infinity, delay: i*0.3 }} />)}</div>}
              {agentState === 'idle' && <span className="w-1.5 h-1.5 rounded-full bg-white/15 animate-pulse" />}
              {isListening && <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-60" /><span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" /></span>}
              {isProcessing && <div className="flex gap-0.5">{[0,1,2].map(i => <motion.span key={i} className="w-1 h-1 rounded-full bg-white/30" animate={{ opacity: [0.2, 0.8, 0.2] }} transition={{ duration: 0.7, repeat: Infinity, delay: i*0.2 }} />)}</div>}
              {isSpeaking && <div className="flex items-center gap-[2px]">{[0,1,2,3].map(i => <motion.span key={i} className="w-[2px] bg-white/30 rounded-full" animate={{ height: [3, 9, 3] }} transition={{ duration: 0.5, repeat: Infinity, delay: i*0.1 }} />)}</div>}
              {statusText && <span className="text-white/30 text-[10px] tracking-wide font-['Jost']">{statusText}</span>}
            </motion.div>
          </div>
        )}

        <div className="absolute top-3 left-3 pointer-events-none z-10">
          <p className="text-white/[0.06] text-[10px] font-['Jost'] font-extralight tracking-[0.3em]">W IA</p>
        </div>
      </div>

      <div className="h-12 overflow-y-auto px-3 py-1 space-y-0.5 bg-black" data-testid="wia-chatlog">
        {messages.slice(-2).map((msg, i) => (
          <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className="flex items-center gap-1.5">
              {msg.image && (
                <img src={msg.image} alt="" className="w-6 h-6 rounded object-cover" />
              )}
              <p className={`text-[10px] max-w-[85%] truncate ${msg.role === 'user' ? 'text-white/25' : 'text-white/15'}`}>
                <span className="font-medium">{msg.role === 'user' ? 'Voce' : 'W'}:</span>{' '}
                {msg.content.substring(0, 80)}{msg.content.length > 80 ? '...' : ''}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Full analysis modal for luxury products */}
      <AnimatePresence>
        {messages.length > 0 && messages[messages.length - 1]?.role === 'assistant' && messages[messages.length - 1]?.content?.includes('📋') && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-20 left-2 right-2 max-h-[60vh] overflow-y-auto bg-black/95 backdrop-blur-xl rounded-2xl border border-white/[0.08] p-4 z-20"
            data-testid="wia-analysis-card"
          >
            <div className="flex justify-between items-start mb-3">
              <h3 className="text-white/80 text-sm font-['Jost'] font-medium">Análise W</h3>
              <button
                onClick={() => setMessages(prev => prev.slice(0, -1))}
                className="text-white/30 hover:text-white/50 transition"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="space-y-3 text-[11px] text-white/70 font-['Jost'] leading-relaxed whitespace-pre-wrap">
              {messages[messages.length - 1].content.split('\n\n').map((section, idx) => (
                <div key={idx} className={`${section.includes('📋') || section.includes('🎯') || section.includes('👔') || section.includes('🔗') ? 'bg-white/[0.03] rounded-xl p-3 border border-white/[0.06]' : ''}`}>
                  {section}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hidden file input - multiple */}
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleFileSelect} 
        accept="image/*" 
        multiple
        className="hidden" 
        data-testid="wia-file-input"
      />

      <div className="px-3 pb-8 bg-black" style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom, 2rem))' }} data-testid="wia-input-bar">
        {/* Attached images preview - shown when input is NOT open */}
        <AnimatePresence>
          {attachedImages.length > 0 && !showTextInput && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }} 
              animate={{ opacity: 1, height: 'auto' }} 
              exit={{ opacity: 0, height: 0 }}
              className="mb-2 p-2 bg-white/[0.03] rounded-xl border border-white/[0.08]"
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-[9px] text-blue-400/60">{attachedImages.length} {attachedImages.length === 1 ? 'imagem' : 'imagens'} para análise</p>
                <button 
                  onClick={clearAllAttachments}
                  className="text-[9px] text-white/30 hover:text-white/50 transition"
                >
                  Limpar todas
                </button>
              </div>
              <div className="flex gap-2 overflow-x-auto">
                {attachedImages.map((img, idx) => (
                  <div key={idx} className="relative flex-shrink-0">
                    <img src={img.preview} alt="" className="w-14 h-14 rounded-lg object-cover" />
                    <button 
                      onClick={() => removeAttachment(idx)}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-black/60 rounded-full flex items-center justify-center text-white/60 hover:text-white/90 transition"
                    >
                      <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ))}
                {attachedImages.length < 5 && (
                  <button 
                    onClick={() => { fileInputRef.current?.click(); setShowTextInput(true); }}
                    className="w-14 h-14 rounded-lg border border-dashed border-white/20 flex items-center justify-center text-white/30 hover:text-white/50 hover:border-white/30 transition flex-shrink-0"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showTextInput ? (
            <motion.div 
              initial={{ opacity: 0, y: 8 }} 
              animate={{ opacity: 1, y: 0 }} 
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.2 }}
              className="relative mx-2"
            >
              {/* Clean minimal input container - transparent with thin border */}
              <div 
                className="relative rounded-[28px]"
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.12)',
                }}
              >
                {/* Attached images preview - TOP */}
                {attachedImages.length > 0 && (
                  <div className="px-5 pt-4 pb-2">
                    <div className="flex gap-3 overflow-x-auto">
                      {attachedImages.map((img, idx) => (
                        <div key={idx} className="relative flex-shrink-0">
                          <img 
                            src={img.preview} 
                            alt="" 
                            className="w-16 h-16 rounded-xl object-cover"
                            style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                          />
                          <button 
                            onClick={() => removeAttachment(idx)}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-black rounded-full flex items-center justify-center border border-white/20"
                          >
                            <svg className="w-3 h-3 text-white/70" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    {/* Pre-made prompt suggestions - shown when image is attached */}
                    {!input && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex flex-wrap gap-2 mt-3"
                      >
                        {IMAGE_PROMPTS.map((prompt, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setInput(prompt)}
                            className="relative px-4 py-2 rounded-full text-xs font-['Jost'] transition-all active:scale-95 overflow-hidden"
                            style={{
                              background: 'transparent',
                              color: 'rgba(255, 255, 255, 0.6)',
                            }}
                            data-testid={`wia-image-prompt-${idx}`}
                          >
                            {/* Animated border light effect for prompt buttons */}
                            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 32" preserveAspectRatio="none">
                              <defs>
                                <linearGradient id={`promptGrad-${idx}`} x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                                  <stop offset="40%" stopColor="rgba(148,163,184,0.4)" />
                                  <stop offset="50%" stopColor="rgba(255,255,255,0.6)" />
                                  <stop offset="60%" stopColor="rgba(148,163,184,0.4)" />
                                  <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                                </linearGradient>
                              </defs>
                              <rect
                                x="0.5" y="0.5" width="99" height="31" rx="15" ry="15"
                                fill="none"
                                stroke={`url(#promptGrad-${idx})`}
                                strokeWidth="1"
                                strokeDasharray="200"
                                className="bar-border-light"
                              />
                            </svg>
                            {/* Thin static border */}
                            <div 
                              className="absolute inset-0 rounded-full pointer-events-none"
                              style={{ border: '1px solid rgba(255, 255, 255, 0.1)' }}
                            />
                            <span className="relative z-10">{prompt}</span>
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </div>
                )}
                
                {/* Textarea */}
                <form onSubmit={handleTextSubmit}>
                  <textarea
                    value={input}
                    onChange={(e) => {
                      setInput(e.target.value);
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
                    }}
                    placeholder="Pergunte ao W Consultor..."
                    className="w-full px-5 pt-4 pb-2 bg-transparent text-lg text-white/90 placeholder:text-white/25 outline-none font-['Jost'] resize-none min-h-[52px] max-h-[100px]"
                    rows={1}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (input.trim()) handleTextSubmit(e);
                      }
                    }}
                    data-testid="wia-text-input"
                  />
                  
                  {/* Bottom action bar - like reference */}
                  <div className="flex items-center justify-between px-3 pb-3">
                    {/* Left buttons */}
                    <div className="flex items-center gap-2">
                      {/* Clip/Attachment */}
                      <button 
                        type="button" 
                        onClick={() => fileInputRef.current?.click()}
                        className="w-11 h-11 rounded-full flex items-center justify-center transition-colors"
                        style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)' }}
                        data-testid="wia-attach-in-form"
                      >
                        <svg className="w-[18px] h-[18px] text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                      </button>
                      
                      {/* Manual/Help button */}
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowManual(true);
                        }}
                        className="w-11 h-11 rounded-full flex items-center justify-center transition-colors"
                        style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)' }}
                        data-testid="wia-manual-btn"
                      >
                        <svg className="w-[18px] h-[18px] text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                        </svg>
                      </button>
                      
                      {/* Export/Share - Open conversation history to select */}
                      <div className="relative">
                        <button 
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (messagesRef.current.filter(m => m.role === 'assistant').length === 0) {
                              alert('Ainda não há respostas do W para exportar. Faça uma pergunta primeiro.');
                              return;
                            }
                            setShowConversationHistory(true);
                          }}
                          className="w-11 h-11 rounded-full flex items-center justify-center transition-colors"
                          style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)' }}
                          data-testid="wia-export-btn"
                        >
                          <svg className="w-[18px] h-[18px] text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    
                    {/* Right buttons */}
                    <div className="flex items-center gap-2">
                      {/* Mic/Control button - tap/hold */}
                      <button 
                        type="button"
                        onClick={() => { 
                          setShowTextInput(false); 
                          handleMicTap(); 
                        }}
                        onTouchStart={handleMicLongPressStart}
                        onTouchEnd={handleMicLongPressEnd}
                        onTouchCancel={handleMicLongPressEnd}
                        onMouseDown={handleMicLongPressStart}
                        onMouseUp={handleMicLongPressEnd}
                        onMouseLeave={handleMicLongPressEnd}
                        className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${(isListening || isSpeaking) ? 'animate-pulse' : ''}`}
                        style={{ 
                          background: isPaused ? 'rgba(251, 191, 36, 0.2)' : (isListening || isSpeaking) ? 'rgba(239, 68, 68, 0.2)' : 'transparent', 
                          border: isPaused ? '1px solid rgba(251, 191, 36, 0.5)' : (isListening || isSpeaking) ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(255,255,255,0.15)' 
                        }}
                      >
                        {isPaused ? (
                          /* Paused - play icon */
                          <svg className="w-[14px] h-[14px] text-amber-400" fill="currentColor" viewBox="0 0 24 24">
                            <polygon points="5,3 19,12 5,21" />
                          </svg>
                        ) : isListening ? (
                          /* Listening - pause icon */
                          <svg className="w-[14px] h-[14px] text-red-400" fill="currentColor" viewBox="0 0 24 24">
                            <rect x="6" y="4" width="4" height="16" rx="1" />
                            <rect x="14" y="4" width="4" height="16" rx="1" />
                          </svg>
                        ) : isSpeaking ? (
                          /* Speaking - stop icon */
                          <svg className="w-[14px] h-[14px] text-red-400" fill="currentColor" viewBox="0 0 24 24">
                            <rect x="6" y="6" width="12" height="12" rx="2" />
                          </svg>
                        ) : isProcessing ? (
                          /* Processing */
                          <svg className="w-[16px] h-[16px] text-white/40 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        ) : (
                          <svg className="w-[18px] h-[18px] text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                          </svg>
                        )}
                      </button>
                      
                      {/* Send */}
                      <button 
                        type="submit" 
                        disabled={!input.trim() || isProcessing}
                        className="w-11 h-11 rounded-full flex items-center justify-center transition-all disabled:opacity-30"
                        style={{ 
                          background: input.trim() ? 'rgba(255,255,255,0.95)' : 'transparent', 
                          border: input.trim() ? 'none' : '1px solid rgba(255,255,255,0.15)' 
                        }}
                        data-testid="wia-send-btn"
                      >
                        <svg 
                          className="w-[18px] h-[18px] transition-colors" 
                          style={{ color: input.trim() ? '#000' : 'rgba(255,255,255,0.5)' }}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </motion.div>
          ) : (
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }} 
              animate={{ opacity: 1, scale: 1 }} 
              className="flex flex-col items-center justify-center w-full"
            >
              {/* CSS for animated border light effect */}
              <style>{`
                @keyframes barBorderLight {
                  0% { stroke-dashoffset: 0; }
                  100% { stroke-dashoffset: -800; }
                }
                .bar-border-light {
                  animation: barBorderLight 6s linear infinite;
                }
              `}</style>
              
              <AnimatePresence mode="wait">
                {barExpanded ? (
                  /* Expanded bar - FIXED width with rotating placeholder text and card-style border */
                  <motion.div
                    key="expanded"
                    initial={{ scaleX: 0.3, opacity: 0 }}
                    animate={{ scaleX: 1, opacity: 1 }}
                    exit={{ scaleX: 0.3, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                    onClick={keepBarActive}
                    className="relative flex items-center gap-3 px-4 py-3 rounded-full origin-center w-[340px]"
                    style={{
                      background: 'rgba(0,0,0,0.85)',
                      border: '1px solid rgba(255,255,255,0.12)',
                    }}
                  >
                    {/* Animated LED light traveling around border - same as WBusiness cards */}
                    {barGlowing && (
                      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 340 56" preserveAspectRatio="none">
                        <defs>
                          <linearGradient id="wiaBarLedGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                            <stop offset="40%" stopColor="rgba(148,163,184,0.6)" />
                            <stop offset="50%" stopColor="rgba(255,255,255,0.95)" />
                            <stop offset="60%" stopColor="rgba(148,163,184,0.6)" />
                            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                          </linearGradient>
                        </defs>
                        <rect x="1" y="1" width="338" height="54" rx="27" ry="27" fill="none" stroke="url(#wiaBarLedGrad)" strokeWidth="1.5" strokeDasharray="800" className="wia-bar-led" />
                      </svg>
                    )}
                    <style>{`
                      @keyframes wiaBarLed {
                        0% { stroke-dashoffset: 0; }
                        100% { stroke-dashoffset: -1600; }
                      }
                      .wia-bar-led {
                        animation: wiaBarLed 8s linear infinite;
                      }
                    `}</style>
                    
                    {/* Clip button */}
                    <button 
                      type="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                        keepBarActive();
                        // Set waiting state and notify user
                        setWaitingForAttachment(true);
                        resetIdleTimer(); // Reset timer while waiting
                        // W IA acknowledges waiting for image
                        if (agentState !== 'ready') {
                          const waitMsg = 'Estou aguardando sua foto para análise...';
                          setMessages(prev => [...prev, { role: 'assistant', content: waitMsg }]);
                          try {
                            const r = await axios.post(`${API}/voice/speak`, { text: waitMsg });
                            if (r.data?.audio) await playAudioBase64(r.data.audio);
                          } catch (err) {}
                        }
                      }}
                      className="w-11 h-11 rounded-full flex items-center justify-center transition-all active:scale-95"
                      style={{ background: 'transparent' }}
                    >
                      <svg className="w-[18px] h-[18px] text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                    </button>

                    {/* Scanner button - opens real QR reader */}
                    <button 
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        keepBarActive();
                        setShowVerifyScanner(true);
                      }}
                      className="w-9 h-9 rounded-full flex items-center justify-center transition-all active:scale-95"
                      style={{ background: 'transparent' }}
                      data-testid="wia-scanner-btn"
                    >
                      <svg className="w-[16px] h-[16px] text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M3 17v2a2 2 0 002 2h2M17 21h2a2 2 0 002-2v-2" />
                        <path strokeLinecap="round" d="M7 12h10" />
                      </svg>
                    </button>

                    {/* Text input placeholder - FIXED width, rotating text */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowTextInput(true);
                        keepBarActive();
                      }}
                      className="flex-1 h-11 px-3 text-left rounded-full transition overflow-hidden"
                      style={{ background: 'transparent' }}
                    >
                      <span className="text-white/40 text-sm font-['Jost'] whitespace-nowrap">
                        {typingPlaceholder || 'Pergunte ao W...'}
                        <span className="animate-pulse ml-0.5 text-white/60">|</span>
                      </span>
                    </button>

                    {/* Mic button */}
                    <button 
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMicTap();
                        keepBarActive();
                      }}
                      onTouchStart={handleMicLongPressStart}
                      onTouchEnd={handleMicLongPressEnd}
                      onTouchCancel={handleMicLongPressEnd}
                      onMouseDown={handleMicLongPressStart}
                      onMouseUp={handleMicLongPressEnd}
                      onMouseLeave={handleMicLongPressEnd}
                      className={`w-11 h-11 rounded-full flex items-center justify-center transition-all active:scale-95 ${(isListening || isSpeaking) ? 'animate-pulse' : ''}`}
                      style={{ 
                        background: isPaused ? 'rgba(251, 191, 36, 0.2)' : (isListening || isSpeaking) ? 'rgba(239, 68, 68, 0.2)' : 'transparent', 
                        border: isPaused ? '1px solid rgba(251, 191, 36, 0.5)' : (isListening || isSpeaking) ? '1px solid rgba(239, 68, 68, 0.5)' : 'none' 
                      }}
                    >
                      {isPaused ? (
                        <svg className="w-[15px] h-[15px] text-amber-400" fill="currentColor" viewBox="0 0 24 24">
                          <polygon points="5,3 19,12 5,21" />
                        </svg>
                      ) : isListening ? (
                        <svg className="w-[15px] h-[15px] text-red-400" fill="currentColor" viewBox="0 0 24 24">
                          <rect x="6" y="4" width="4" height="16" rx="1" />
                          <rect x="14" y="4" width="4" height="16" rx="1" />
                        </svg>
                      ) : isSpeaking ? (
                        <svg className="w-[15px] h-[15px] text-red-400" fill="currentColor" viewBox="0 0 24 24">
                          <rect x="6" y="6" width="12" height="12" rx="2" />
                        </svg>
                      ) : isProcessing ? (
                        <svg className="w-[17px] h-[17px] text-white/40 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : (
                        <svg className="w-[18px] h-[18px] text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                        </svg>
                      )}
                    </button>
                  </motion.div>
                ) : (
                  /* Collapsed bar - with animated border light effect like cards */
                  <motion.div
                    key="collapsed"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    onClick={expandBar}
                    className="relative flex items-center gap-2 px-4 py-3 rounded-full cursor-pointer hover:scale-105 transition-transform"
                    style={{
                      background: 'rgba(0,0,0,0.85)',
                      border: '1px solid rgba(255,255,255,0.12)',
                    }}
                  >
                    {/* Animated LED light traveling around border - same as WBusiness cards */}
                    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 140 56" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="wiaMiniLedGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                          <stop offset="40%" stopColor="rgba(148,163,184,0.6)" />
                          <stop offset="50%" stopColor="rgba(255,255,255,0.95)" />
                          <stop offset="60%" stopColor="rgba(148,163,184,0.6)" />
                          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                        </linearGradient>
                      </defs>
                      <rect x="1" y="1" width="138" height="54" rx="27" ry="27" fill="none" stroke="url(#wiaMiniLedGrad)" strokeWidth="1.5" strokeDasharray="600" className="wia-mini-led" />
                    </svg>
                    <style>{`
                      @keyframes wiaMiniLed {
                        0% { stroke-dashoffset: 0; }
                        100% { stroke-dashoffset: -1200; }
                      }
                      .wia-mini-led {
                        animation: wiaMiniLed 8s linear infinite;
                      }
                    `}</style>
                    
                    {/* Clip icon */}
                    <div className="w-11 h-11 rounded-full flex items-center justify-center">
                      <svg className="w-[18px] h-[18px] text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                    </div>
                    {/* Mic icon */}
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center ${(isListening || isSpeaking) ? 'animate-pulse' : ''}`}
                      style={{ 
                        background: (isListening || isSpeaking) ? 'rgba(239, 68, 68, 0.2)' : 'transparent',
                      }}
                    >
                      {isListening || isSpeaking ? (
                        <svg className="w-[14px] h-[14px] text-red-400" fill="currentColor" viewBox="0 0 24 24">
                          <rect x="6" y="6" width="12" height="12" rx="2" />
                        </svg>
                      ) : (
                        <svg className="w-[18px] h-[18px] text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                        </svg>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Authenticity Result Modal */}
      <AnimatePresence>
        {authenticityResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setAuthenticityResult(null)}
            data-testid="authenticity-modal"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-sm"
            >
              {/* Close button - outside the card for PNG export */}
              <button
                onClick={() => setAuthenticityResult(null)}
                className="absolute -top-10 right-0 p-2 text-white/50 hover:text-white/80 transition z-20"
                data-testid="close-authenticity-modal"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>

              {/* Card content - this will be exported as PNG */}
              <div 
                ref={authenticityCardRef}
                className="relative rounded-3xl overflow-hidden"
                style={{ backgroundColor: '#0a0d10' }}
              >
                {/* Animated border light effect - same as member cards */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none z-10" viewBox="0 0 400 500" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="authCardGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                      <stop offset="40%" stopColor="rgba(59,130,246,0.6)" />
                      <stop offset="50%" stopColor="rgba(255,255,255,0.9)" />
                      <stop offset="60%" stopColor="rgba(148,163,184,0.6)" />
                      <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                    </linearGradient>
                  </defs>
                  <rect
                    x="1" y="1" width="398" height="498" rx="24" ry="24"
                    fill="none"
                    stroke="url(#authCardGrad)"
                    strokeWidth="2"
                    strokeDasharray="1800"
                    className="auth-border-light"
                  />
                </svg>
                <style>{`
                  @keyframes authBorderLight {
                    0% { stroke-dashoffset: 0; }
                    100% { stroke-dashoffset: -3600; }
                  }
                  .auth-border-light {
                    animation: authBorderLight 8s linear infinite;
                  }
                `}</style>

                {/* Inner background */}
                <div className="bg-gradient-to-br from-[#0f1419] to-[#0a0d10] border border-white/[0.08] rounded-3xl">
                  {/* Header with W logo and percentage */}
                  <div className="p-8 pb-6 text-center border-b border-white/[0.06]">
                    <div className="relative w-28 h-28 mx-auto mb-4">
                      {/* Circular progress background */}
                      <svg className="w-28 h-28 transform -rotate-90">
                        <circle cx="56" cy="56" r="50" fill="none" stroke="white" strokeOpacity="0.05" strokeWidth="6" />
                        <circle 
                          cx="56" cy="56" r="50" fill="none" 
                          stroke={authenticityResult.percentage >= 80 ? '#3b82f6' : authenticityResult.percentage >= 50 ? '#64748b' : '#94a3b8'}
                          strokeWidth="6" 
                          strokeLinecap="round"
                          strokeDasharray={`${(authenticityResult.percentage / 100) * 314} 314`}
                        />
                      </svg>
                      {/* W Logo in center - SVG inline for reliable PNG export */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <svg className="w-14 h-14" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <linearGradient id="wLogoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" stopColor="#ffffff" />
                              <stop offset="50%" stopColor="#94a3b8" />
                              <stop offset="100%" stopColor="#64748b" />
                            </linearGradient>
                          </defs>
                          <text x="50" y="72" textAnchor="middle" fill="url(#wLogoGrad)" fontSize="65" fontWeight="bold" fontFamily="Jost, sans-serif">W</text>
                        </svg>
                      </div>
                    </div>
                    
                    {/* Percentage below the circle */}
                    <div className="mb-3">
                      <span className={`text-4xl font-['Jost'] font-semibold`} style={{ color: authenticityResult.percentage >= 80 ? '#60a5fa' : authenticityResult.percentage >= 50 ? '#94a3b8' : '#64748b' }}>
                        {authenticityResult.percentage}%
                      </span>
                    </div>
                    
                    <p className="text-xs font-['Jost'] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.3)' }}>Analise de Autenticidade</p>
                    <h3 className="text-lg font-['Jost'] font-medium mt-1" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      {authenticityResult.percentage >= 80 ? 'Provavelmente Autentico' : 
                       authenticityResult.percentage >= 50 ? 'Verificacao Recomendada' : 'Suspeita de Falsificacao'}
                    </h3>
                  </div>

                  {/* Analysis text - without emojis, full height for export */}
                  <div className="p-6 max-h-[35vh] overflow-y-auto" id="analysis-text-container">
                    <p className="text-sm font-['Jost'] leading-relaxed whitespace-pre-line" style={{ color: 'rgba(255,255,255,0.6)' }}>
                      {authenticityResult.text.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]/gu, '').trim()}
                    </p>
                  </div>

                  {/* WClub branding footer with QR + hash */}
                  <div className="px-6 py-3 border-t border-white/[0.06]">
                    <div className="flex items-end justify-between">
                      <div>
                        <span className="text-[10px] font-['Jost'] block" style={{ color: 'rgba(255,255,255,0.25)' }}>Verificado por W IA</span>
                        {authenticityResult.hash && (
                          <span className="text-[9px] font-mono block mt-0.5" style={{ color: 'rgba(255,255,255,0.15)' }}>#{authenticityResult.hash}</span>
                        )}
                        <span className="text-[9px] font-['Jost'] block mt-0.5" style={{ color: 'rgba(255,255,255,0.12)' }}>WClub</span>
                      </div>
                      {authenticityResult.qrDataUrl && (
                        <div className="flex-shrink-0 rounded-lg overflow-hidden p-1" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.06)' }}>
                          <img src={authenticityResult.qrDataUrl} alt="QR" className="w-11 h-11" style={{ imageRendering: 'pixelated' }} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer icons - white outline, liquid glass */}
              <div className="mt-4 flex justify-center gap-5">
                {/* Share PNG */}
                <motion.button
                  whileTap={{ scale: 0.85 }}
                  onClick={async () => {
                    if (!authenticityCardRef.current) return;
                    
                    try {
                      // Temporarily expand the text container for full capture
                      const textContainer = authenticityCardRef.current.querySelector('#analysis-text-container');
                      const originalMaxHeight = textContainer?.style.maxHeight;
                      const originalOverflow = textContainer?.style.overflow;
                      if (textContainer) {
                        textContainer.style.maxHeight = 'none';
                        textContainer.style.overflow = 'visible';
                      }
                      
                      // Small delay to ensure styles are applied
                      await new Promise(r => setTimeout(r, 100));
                      
                      // Pre-load the logo image to ensure it's in the PNG
                      const logoImg = authenticityCardRef.current.querySelector('img[alt="W"]');
                      if (logoImg) {
                        // Force image reload with cache bust
                        const originalSrc = logoImg.src;
                        logoImg.src = originalSrc + '?t=' + Date.now();
                        await new Promise(r => {
                          logoImg.onload = r;
                          logoImg.onerror = r;
                          setTimeout(r, 500); // Timeout fallback
                        });
                      }
                      
                      // Generate PNG from the card with better options
                      const dataUrl = await toPng(authenticityCardRef.current, {
                        quality: 1.0,
                        backgroundColor: '#0a0d10',
                        pixelRatio: 3, // Higher quality for retina
                        cacheBust: true,
                        skipFonts: false,
                        includeQueryParams: true,
                        fetchRequestInit: {
                          mode: 'cors',
                          cache: 'no-cache',
                        },
                        style: {
                          transform: 'none',
                        }
                      });
                      
                      // Restore original styles
                      if (textContainer) {
                        textContainer.style.maxHeight = originalMaxHeight || '';
                        textContainer.style.overflow = originalOverflow || '';
                      }
                      
                      // Convert dataUrl to Blob
                      const res = await fetch(dataUrl);
                      const blob = await res.blob();
                      const file = new File([blob], 'wclub-autenticidade.png', { type: 'image/png' });
                      
                      // Try native share with file
                      if (navigator.canShare && navigator.canShare({ files: [file] })) {
                        await navigator.share({
                          title: 'Analise de Autenticidade - WClub',
                          text: `Resultado: ${authenticityResult.percentage}% de autenticidade`,
                          files: [file],
                        });
                      } else if (navigator.share) {
                        // Fallback: share text only
                        await navigator.share({
                          title: 'Analise de Autenticidade - WClub',
                          text: `Resultado da analise W: ${authenticityResult.percentage}% de autenticidade.\n\n${authenticityResult.text.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]/gu, '').trim()}`,
                        });
                      } else {
                        // Desktop fallback: download the image
                        const link = document.createElement('a');
                        link.download = 'wclub-autenticidade.png';
                        link.href = dataUrl;
                        link.click();
                      }
                    } catch (err) {
                      if (err.name !== 'AbortError') {
                        console.error('Erro ao compartilhar:', err);
                        // Fallback: try to download
                        try {
                          const dataUrl = await toPng(authenticityCardRef.current, { quality: 0.95, backgroundColor: '#0a0d10' });
                          const link = document.createElement('a');
                          link.download = 'wclub-autenticidade.png';
                          link.href = dataUrl;
                          link.click();
                        } catch (e) {}
                      }
                    }
                  }}
                  className="w-12 h-12 rounded-full flex items-center justify-center transition active:opacity-60"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)' }}
                  data-testid="share-authenticity-png"
                >
                  <svg className="w-5 h-5 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                </motion.button>
                
                {/* Export analyzed image with percentage overlay */}
                {authenticityResult?.images?.length > 0 && (
                  <motion.button
                    whileTap={{ scale: 0.85 }}
                    onClick={async () => {
                      try {
                        const imageUrl = authenticityResult.images[0];
                        const dataUrl = await generateImageWithPercentage(imageUrl, authenticityResult.percentage);
                        
                        // Convert to blob
                        const res = await fetch(dataUrl);
                        const blob = await res.blob();
                        const file = new File([blob], 'wclub-foto-verificada.png', { type: 'image/png' });
                        
                        // Try native share
                        if (navigator.canShare && navigator.canShare({ files: [file] })) {
                          await navigator.share({
                            title: 'Foto Verificada - WClub',
                            text: `Autenticidade: ${authenticityResult.percentage}%`,
                            files: [file],
                          });
                        } else {
                          // Download fallback
                          const link = document.createElement('a');
                          link.download = 'wclub-foto-verificada.png';
                          link.href = dataUrl;
                          link.click();
                        }
                      } catch (err) {
                        console.error('Erro ao exportar foto:', err);
                      }
                    }}
                    className="w-12 h-12 rounded-full flex items-center justify-center transition active:opacity-60"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)' }}
                    data-testid="export-photo-with-percentage"
                  >
                    <svg className="w-5 h-5 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </motion.button>
                )}
                
                <motion.button
                  whileTap={{ scale: 0.85 }}
                  onClick={() => setAuthenticityResult(null)}
                  className="w-12 h-12 rounded-full flex items-center justify-center transition active:opacity-60"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)' }}
                  data-testid="close-authenticity"
                >
                  <svg className="w-5 h-5 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Response PNG Modal - for "Gere PNG da resposta" command */}
      <AnimatePresence>
        {showResponsePNGCard && responseForPNG?.conversations?.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 pt-16 overflow-y-auto"
            onClick={() => setShowResponsePNGCard(false)}
            data-testid="response-png-modal"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-sm mb-8"
            >
              {/* Close button */}
              <button
                onClick={() => setShowResponsePNGCard(false)}
                className="absolute -top-10 right-0 p-2 text-white/50 hover:text-white/80 transition z-20"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>

              {/* Card content for PNG export */}
              <div 
                ref={responsePNGCardRef}
                className="relative rounded-3xl overflow-hidden"
                style={{ backgroundColor: '#0a0d10' }}
              >
                {/* Animated border */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none z-10" viewBox="0 0 400 600" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="responsePNGGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                      <stop offset="40%" stopColor="rgba(148,163,184,0.6)" />
                      <stop offset="50%" stopColor="rgba(255,255,255,0.9)" />
                      <stop offset="60%" stopColor="rgba(100,116,139,0.6)" />
                      <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                    </linearGradient>
                  </defs>
                  <rect x="1" y="1" width="398" height="598" rx="24" ry="24" fill="none" stroke="url(#responsePNGGrad)" strokeWidth="2" strokeDasharray="2000" className="response-border-light" />
                </svg>
                <style>{`
                  @keyframes responseBorderLight {
                    0% { stroke-dashoffset: 0; }
                    100% { stroke-dashoffset: -4000; }
                  }
                  .response-border-light {
                    animation: responseBorderLight 8s linear infinite;
                  }
                `}</style>

                {/* Inner content */}
                <div className="bg-gradient-to-br from-[#0f1419] to-[#0a0d10] border border-white/[0.08] rounded-3xl p-6">
                  {/* Header with W Logo PNG */}
                  <div className="flex items-center gap-3 mb-5 pb-4 border-b border-white/[0.06]">
                    {logoBase64 ? (
                      <img 
                        src={logoBase64} 
                        alt="W" 
                        className="w-10 h-10 object-contain"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                        <span className="text-white font-bold text-lg">W</span>
                      </div>
                    )}
                    <div>
                      <p className="text-white/90 font-['Jost'] text-sm font-medium">W - Consultor WClub</p>
                      <p className="text-white/30 text-[10px]">{new Date(responseForPNG.timestamp).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>

                  {/* Conversations list - no height limit for PNG capture */}
                  <div className="space-y-4">
                    {responseForPNG.conversations?.map((conv, idx) => (
                      <div key={idx} className={idx > 0 ? 'pt-4 border-t border-white/[0.06]' : ''}>
                        {/* Question */}
                        {conv.question && (
                          <div className="mb-3">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </div>
                              <span className="text-white/50 text-xs font-['Jost']">Você perguntou:</span>
                            </div>
                            <p className="text-white/70 text-sm font-['Jost'] leading-relaxed pl-8 italic">
                              "{conv.question}"
                            </p>
                          </div>
                        )}

                        {/* Response */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            {logoBase64 ? (
                              <img src={logoBase64} alt="W" className="w-6 h-6 object-contain" />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                                <span className="text-white font-bold text-xs">W</span>
                              </div>
                            )}
                            <span className="text-white/50 text-xs font-['Jost']">W respondeu:</span>
                          </div>
                          <p className="text-white/80 text-sm font-['Jost'] leading-relaxed pl-8 whitespace-pre-wrap">
                            {conv.response}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Footer with QR + hash + branding */}
                  <div className="mt-6 pt-4 border-t border-white/[0.06]">
                    <div className="flex items-end justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {logoBase64 ? (
                            <img src={logoBase64} alt="W" className="w-4 h-4 object-contain opacity-40" />
                          ) : (
                            <span className="text-white/40 text-xs font-bold">W</span>
                          )}
                          <span className="text-[10px] text-white/30 font-['Jost']">Verificado por W IA</span>
                        </div>
                        {responseForPNG.hash && (
                          <p className="text-[9px] text-white/20 font-['Jost'] font-mono">#{responseForPNG.hash}</p>
                        )}
                        <p className="text-[9px] text-white/15 font-['Jost'] mt-0.5">WClub</p>
                      </div>
                      {responseForPNG.qrDataUrl && (
                        <div className="flex-shrink-0 ml-3">
                          <div className="rounded-lg overflow-hidden p-1" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.06)' }}>
                            <img src={responseForPNG.qrDataUrl} alt="QR" className="w-12 h-12" style={{ imageRendering: 'pixelated' }} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Share buttons - icon only, liquid glass */}
              <div className="flex justify-center gap-5 mt-4">
                <motion.button
                  whileTap={{ scale: 0.85 }}
                  onClick={async () => {
                    try {
                      const dataUrl = await toPng(responsePNGCardRef.current, {
                        quality: 1.0,
                        backgroundColor: '#0a0d10',
                        pixelRatio: 3,
                        cacheBust: true,
                        includeQueryParams: true,
                        skipFonts: true,
                        fetchRequestInit: {
                          mode: 'cors',
                          credentials: 'same-origin'
                        }
                      });
                      
                      const res = await fetch(dataUrl);
                      const blob = await res.blob();
                      const file = new File([blob], 'wclub-resposta-w.png', { type: 'image/png' });
                      
                      if (navigator.canShare && navigator.canShare({ files: [file] })) {
                        await navigator.share({
                          title: 'Resposta W - WClub',
                          files: [file],
                        });
                      } else if (navigator.share) {
                        await navigator.share({
                          title: 'Resposta W - WClub',
                          text: responseForPNG.text,
                        });
                      } else {
                        const link = document.createElement('a');
                        link.download = 'wclub-resposta-w.png';
                        link.href = dataUrl;
                        link.click();
                      }
                    } catch (err) {
                      if (err.name !== 'AbortError') {
                        console.error('Erro ao compartilhar:', err);
                      }
                    }
                  }}
                  className="w-12 h-12 rounded-full flex items-center justify-center transition active:opacity-60"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)' }}
                  data-testid="share-response-png"
                >
                  <svg className="w-5 h-5 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.85 }}
                  onClick={() => setShowResponsePNGCard(false)}
                  className="w-12 h-12 rounded-full flex items-center justify-center transition active:opacity-60"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)' }}
                >
                  <svg className="w-5 h-5 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Conversation History Modal - Select which conversation to export */}
      <AnimatePresence>
        {showConversationHistory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-xl flex flex-col"
            onClick={() => setShowConversationHistory(false)}
          >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 flex flex-col max-h-screen overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/[0.08]">
                <div className="flex items-center gap-3">
                  <img src="/w-logo.png" alt="W" className="w-8 h-8 object-contain" />
                  <div>
                    <h2 className="text-white font-['Jost'] text-lg font-medium">Selecionar Conversa</h2>
                    <p className="text-white/40 text-xs">Toque na conversa que deseja exportar</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowConversationHistory(false)}
                  className="w-10 h-10 rounded-full bg-white/[0.06] flex items-center justify-center"
                >
                  <svg className="w-5 h-5 text-white/60" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              {/* Conversation List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {/* Select All / Deselect All buttons */}
                {messages.filter(m => m.role === 'assistant').length > 1 && (
                  <div className="flex items-center justify-between mb-2 pb-2 border-b border-white/[0.06]">
                    <span className="text-white/40 text-xs font-['Jost']">
                      {selectedMessageIndices.length} selecionada{selectedMessageIndices.length !== 1 ? 's' : ''}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const allIndices = messages
                            .map((m, i) => m.role === 'assistant' ? i : null)
                            .filter(i => i !== null);
                          setSelectedMessageIndices(allIndices);
                        }}
                        className="text-blue-400 text-xs font-['Jost'] hover:text-blue-300 transition"
                      >
                        Selecionar todas
                      </button>
                      <span className="text-white/20">|</span>
                      <button
                        onClick={() => setSelectedMessageIndices([])}
                        className="text-white/40 text-xs font-['Jost'] hover:text-white/60 transition"
                      >
                        Limpar
                      </button>
                    </div>
                  </div>
                )}

                {messages.map((msg, index) => {
                  // Only show assistant messages (responses)
                  if (msg.role !== 'assistant') return null;
                  
                  // Find the corresponding user question (previous message)
                  const questionIndex = index - 1;
                  const question = questionIndex >= 0 && messages[questionIndex]?.role === 'user' 
                    ? messages[questionIndex].content 
                    : '';
                  
                  const isSelected = selectedMessageIndices.includes(index);
                  const msgKey = `msg-${index}-${msg.content?.substring(0, 20) || 'empty'}`;
                  
                  return (
                    <motion.div
                      key={msgKey}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedMessageIndices(prev => prev.filter(i => i !== index));
                        } else {
                          setSelectedMessageIndices(prev => [...prev, index]);
                        }
                      }}
                      className={`relative p-4 rounded-2xl cursor-pointer transition-all ${
                        isSelected 
                          ? 'bg-blue-500/20 border-2 border-blue-500/50' 
                          : 'bg-white/[0.03] border border-white/[0.08] hover:bg-white/[0.06]'
                      }`}
                    >
                      {/* Selection indicator */}
                      <div className={`absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                        isSelected ? 'bg-blue-500' : 'bg-white/10 border border-white/20'
                      }`}>
                        {isSelected && (
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      
                      {/* Question */}
                      {question && (
                        <div className="mb-3 pb-3 border-b border-white/[0.06]">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                            <span className="text-white/40 text-[10px] font-['Jost']">Você perguntou</span>
                          </div>
                          <p className="text-white/60 text-xs font-['Jost'] pl-7 line-clamp-2">
                            {question}
                          </p>
                        </div>
                      )}
                      
                      {/* Response */}
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <img src="/w-logo.png" alt="W" className="w-5 h-5 object-contain" />
                          <span className="text-white/40 text-[10px] font-['Jost']">W respondeu</span>
                        </div>
                        <p className="text-white/80 text-xs font-['Jost'] pl-7 line-clamp-3">
                          {msg.content}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
                
                {messages.filter(m => m.role === 'assistant').length === 0 && (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/[0.03] flex items-center justify-center">
                      <img src="/w-logo.png" alt="W" className="w-8 h-8 object-contain opacity-30" />
                    </div>
                    <p className="text-white/30 text-sm font-['Jost']">Nenhuma conversa ainda</p>
                    <p className="text-white/20 text-xs font-['Jost'] mt-1">Faça uma pergunta ao W primeiro</p>
                  </div>
                )}
              </div>

              {/* Export Button - Fixed at bottom */}
              {selectedMessageIndices.length > 0 && (
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  className="p-4 border-t border-white/[0.08] bg-black/80 backdrop-blur-xl"
                >
                  <button
                    onClick={() => {
                      // Build array of conversations to export
                      const conversations = selectedMessageIndices
                        .sort((a, b) => a - b) // Sort by order in chat
                        .map(index => {
                          const selectedMsg = messages[index];
                          const questionIndex = index - 1;
                          const question = questionIndex >= 0 && messages[questionIndex]?.role === 'user' 
                            ? messages[questionIndex].content 
                            : '';
                          return { question, response: selectedMsg.content };
                        });
                      
                      setResponseForPNG({ 
                        conversations: conversations,
                        timestamp: new Date().toISOString() 
                      });
                      setShowConversationHistory(false);
                      setSelectedMessageIndices([]);
                      setShowResponsePNGCard(true);
                    }}
                    className="w-full py-4 rounded-2xl text-white font-['Jost'] font-medium flex items-center justify-center gap-3"
                    style={{
                      background: 'linear-gradient(135deg, rgba(59,130,246,0.9) 0%, rgba(37,99,235,0.9) 100%)',
                      boxShadow: '0 0 30px rgba(59,130,246,0.4)'
                    }}
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Exportar {selectedMessageIndices.length} conversa{selectedMessageIndices.length > 1 ? 's' : ''} como PNG
                  </button>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Manual/Instructions Modal */}
      <AnimatePresence>
        {showManual && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-xl flex flex-col"
            onClick={() => setShowManual(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 flex flex-col max-w-md mx-auto w-full p-4 pt-safe"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-white font-['Jost'] font-medium">Manual do W</h2>
                    <p className="text-white/40 text-xs font-['Jost']">Como usar o assistente</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowManual(false)}
                  className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center"
                >
                  <svg className="w-5 h-5 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto space-y-6">
                {/* Section: Start */}
                <div className="bg-white/[0.03] rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                      <span className="text-green-400 text-sm font-bold">1</span>
                    </div>
                    <h3 className="text-white/80 font-['Jost'] font-medium text-sm">Iniciar Conversa</h3>
                  </div>
                  <ul className="space-y-2 text-white/50 text-xs font-['Jost'] pl-8">
                    <li>• Toque no <span className="text-white/70">orb central</span> ou no <span className="text-white/70">microfone</span></li>
                    <li>• Aguarde o W cumprimentar você</li>
                    <li>• Fale sua pergunta claramente</li>
                  </ul>
                </div>

                {/* Section: During */}
                <div className="bg-white/[0.03] rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <span className="text-blue-400 text-sm font-bold">2</span>
                    </div>
                    <h3 className="text-white/80 font-['Jost'] font-medium text-sm">Durante a Conversa</h3>
                  </div>
                  <ul className="space-y-2 text-white/50 text-xs font-['Jost'] pl-8">
                    <li>• <span className="text-amber-400">⏸ Pausar:</span> Toque no mic para pausar</li>
                    <li>• <span className="text-green-400">▶ Continuar:</span> Toque novamente para retomar</li>
                    <li>• <span className="text-red-400">■ Parar W:</span> Quando W fala, toque para interromper</li>
                    <li>• <span className="text-red-400">■ Desligar:</span> Segure o mic por 1s</li>
                  </ul>
                </div>

                {/* Section: End */}
                <div className="bg-white/[0.03] rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center">
                      <span className="text-red-400 text-sm font-bold">3</span>
                    </div>
                    <h3 className="text-white/80 font-['Jost'] font-medium text-sm">Finalizar Conversa</h3>
                  </div>
                  <ul className="space-y-2 text-white/50 text-xs font-['Jost'] pl-8">
                    <li>• Diga: <span className="text-white/70">"Ok"</span>, <span className="text-white/70">"Pronto"</span>, <span className="text-white/70">"Valeu"</span></li>
                    <li>• Ou: <span className="text-white/70">"Ok W, obrigado"</span></li>
                    <li>• Ou: <span className="text-white/70">"Ok W, desligar"</span></li>
                    <li>• Ou segure o mic por 1 segundo</li>
                  </ul>
                </div>

                {/* Section: Features */}
                <div className="bg-white/[0.03] rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <svg className="w-3 h-3 text-purple-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    </div>
                    <h3 className="text-white/80 font-['Jost'] font-medium text-sm">Recursos Extras</h3>
                  </div>
                  <ul className="space-y-2 text-white/50 text-xs font-['Jost'] pl-8">
                    <li>• <span className="text-white/70">Anexar fotos:</span> Use o clipe para analisar produtos</li>
                    <li>• <span className="text-white/70">Exportar PNG:</span> Compartilhe respostas do W</li>
                    <li>• <span className="text-white/70">Texto:</span> Digite se preferir não falar</li>
                  </ul>
                </div>
              </div>

              {/* Footer */}
              <div className="pt-4">
                <button
                  onClick={() => setShowManual(false)}
                  className="w-full py-4 rounded-2xl bg-white/[0.06] border border-white/[0.08] text-white/70 font-['Jost'] font-medium text-sm"
                >
                  Entendi
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* QR Verify Scanner Modal */}
      <AnimatePresence>
        {showVerifyScanner && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] flex flex-col"
            style={{ background: 'rgba(0,0,0,0.95)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3" style={{ paddingTop: 'max(env(safe-area-inset-top, 12px), 12px)' }}>
              <button onClick={() => { setShowVerifyScanner(false); setVerifyResult(null); }}
                className="text-blue-400 text-sm font-['Jost']">Fechar</button>
              <span className="text-white/80 font-['Jost'] font-semibold text-sm">Verificar Selo W</span>
              <div className="w-14" />
            </div>

            {verifyResult ? (
              /* Verification Result */
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                className="flex-1 flex items-center justify-center p-6">
                <div className="w-full max-w-xs rounded-3xl p-6 text-center"
                  style={{ background: 'rgba(15,20,35,0.95)', border: `1px solid ${verifyResult.verified ? 'rgba(59,130,246,0.2)' : 'rgba(239,68,68,0.2)'}` }}>
                  <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{ background: verifyResult.verified ? 'rgba(59,130,246,0.12)' : 'rgba(239,68,68,0.12)', border: `1.5px solid ${verifyResult.verified ? 'rgba(59,130,246,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
                    {verifyResult.verified ? (
                      <svg className="w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    ) : (
                      <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    )}
                  </div>
                  {verifyResult.verified ? (
                    <>
                      <p className="text-emerald-400 text-sm font-semibold font-['Jost'] mb-1">Selo Verificado</p>
                      <p className="text-white/80 text-lg font-bold font-['Jost']">{verifyResult.percentage}%</p>
                      <p className="text-white/30 text-[10px] font-['Jost'] mt-1">#{verifyResult.hash}</p>
                      {verifyResult.member_name && <p className="text-white/40 text-xs font-['Jost'] mt-2">{verifyResult.member_name}</p>}
                      {verifyResult.date && <p className="text-white/20 text-[10px] font-['Jost']">{new Date(verifyResult.date).toLocaleDateString('pt-BR')}</p>}
                    </>
                  ) : (
                    <>
                      <p className="text-red-400 text-sm font-semibold font-['Jost'] mb-1">Não Verificado</p>
                      <p className="text-white/40 text-xs font-['Jost']">{verifyResult.message || 'Selo não encontrado'}</p>
                    </>
                  )}
                  <button onClick={() => { setVerifyResult(null); }}
                    className="mt-4 w-full py-2.5 rounded-xl text-sm font-['Jost'] text-blue-400/70 transition active:bg-blue-500/10"
                    style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.12)' }}>
                    Escanear outro
                  </button>
                </div>
              </motion.div>
            ) : (
              /* QR Camera Scanner */
              <div className="flex-1 flex flex-col">
                <div className="flex-1 relative overflow-hidden">
                  <Scanner
                    onScan={async (result) => {
                      if (!result?.[0]) return;
                      const data = result[0].rawValue;
                      // Extract hash from verify URL or raw hash
                      let hash = data;
                      if (data.includes('/verify/')) {
                        hash = data.split('/verify/')[1];
                      }
                      if (!hash || hash.length < 6) {
                        setVerifyResult({ verified: false, message: 'QR inválido' });
                        return;
                      }
                      try {
                        const r = await axios.get(`${API}/w-verify/${hash}`);
                        setVerifyResult(r.data);
                        if (navigator.vibrate) navigator.vibrate(r.data.verified ? [100] : [100, 50, 100]);
                      } catch (e) {
                        setVerifyResult({ verified: false, message: 'Erro ao verificar' });
                      }
                    }}
                    onError={(err) => console.error('[QR Scan]', err)}
                    formats={['qr_code']}
                    components={{ audio: false, torch: true }}
                    styles={{ container: { width: '100%', height: '100%' } }}
                  />
                  {/* Scanner overlay frame */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-52 h-52 relative">
                      <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-blue-400 rounded-tl-lg" />
                      <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-blue-400 rounded-tr-lg" />
                      <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-blue-400 rounded-bl-lg" />
                      <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-blue-400 rounded-br-lg" />
                      <motion.div
                        animate={{ y: [0, 192, 0] }}
                        transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                        className="absolute left-2 right-2 h-[2px] bg-blue-400/60 rounded-full"
                        style={{ boxShadow: '0 0 8px rgba(59,130,246,0.5)' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Gallery option - decodes QR from selected image (client + backend fallback) */}
                <div className="px-4 py-4 flex justify-center">
                  <button onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = async (ev) => {
                      const file = ev.target.files?.[0];
                      if (!file) return;
                      const tryClientDecode = async () => {
                        try {
                          const jsQR = (await import('jsqr')).default;
                          const img = new Image();
                          img.src = URL.createObjectURL(file);
                          await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
                          const maxW = 1600;
                          const ratio = Math.min(1, maxW / img.width);
                          const cw = Math.floor(img.width * ratio);
                          const ch = Math.floor(img.height * ratio);
                          const canvas = document.createElement('canvas');
                          canvas.width = cw; canvas.height = ch;
                          const ctx = canvas.getContext('2d');
                          ctx.drawImage(img, 0, 0, cw, ch);
                          const full = ctx.getImageData(0, 0, cw, ch);
                          let code = jsQR(full.data, cw, ch, { inversionAttempts: 'attemptBoth' });
                          if (!code) {
                            const qx = Math.floor(cw * 0.5);
                            const qy = Math.floor(ch * 0.5);
                            const q = ctx.getImageData(qx, qy, cw - qx, ch - qy);
                            code = jsQR(q.data, cw - qx, ch - qy, { inversionAttempts: 'attemptBoth' });
                          }
                          URL.revokeObjectURL(img.src);
                          return code ? code.data : null;
                        } catch (e) {
                          console.warn('[client qr]', e);
                          return null;
                        }
                      };
                      const tryBackendDecode = async () => {
                        try {
                          const fd = new FormData();
                          fd.append('image', file);
                          const r = await axios.post(`${API}/wingman/qr/decode`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                          if (r.data?.ok) return r.data.data;
                        } catch (e) {
                          console.warn('[backend qr]', e);
                        }
                        return null;
                      };
                      let data = await tryClientDecode();
                      if (!data) data = await tryBackendDecode();
                      if (!data) {
                        setVerifyResult({ verified: false, message: 'QR não encontrado na imagem. Tente uma foto mais nítida.' });
                        return;
                      }
                      let hash = data;
                      if (data.includes('/verify/')) hash = data.split('/verify/')[1].split(/[?#]/)[0];
                      // Aggressive sanitization: strip any non-alphanumeric char
                      hash = (hash || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                      if (!hash || hash.length < 6) {
                        setVerifyResult({ verified: false, message: 'QR inválido: ' + data.slice(0, 40) });
                        return;
                      }
                      try {
                        const r = await axios.get(`${API}/w-verify/${hash}`);
                        setVerifyResult(r.data);
                        if (navigator.vibrate) navigator.vibrate(r.data.verified ? [100] : [100, 50, 100]);
                      } catch (e) {
                        setVerifyResult({ verified: false, message: 'Erro ao verificar hash ' + hash });
                      }
                    };
                    input.click();
                  }}
                    data-testid="gallery-qr-btn"
                    className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-['Jost'] text-white/80 transition active:bg-white/5"
                    style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)' }}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v13.5A1.5 1.5 0 003.75 21z" />
                    </svg>
                    Buscar da galeria
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WIA;
